import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { Text, Heading, Input, Select, Button, SortableTableHeader, LoadingSpinner, Tooltip } from '../components/ui'
import { Sidebar } from '../components/ui'
import { usePermission } from '../hooks/usePermissions'
import { useUserLogs } from '../hooks/useUserLogs'
import { supabase } from '../lib/supabase'
import { modulesService } from '../services/modules'
import { ResponsiveContainer, BarChart, Bar, CartesianGrid, XAxis, YAxis, Tooltip as RechartsTooltip, PieChart, Pie, Cell, Legend } from 'recharts'
import { 
  ExclamationTriangleIcon, 
  ChevronDownIcon,
  ChevronUpIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  PaperAirplaneIcon,
  QuestionMarkCircleIcon,
  FunnelIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline'

interface CategoriaData {
  id: number
  nome: string | null
}

interface ConvencaoData {
  id: number
  created_at: string
  cnpj_entidade_laboral: string | null
  cnpj_entidade_patronal: string | null
  id_scgp: number | null
  data_inicio_vigencia: string | null
  data_fim_vigencia: string | null
  status: string | null
  status_mte: string | null
  data_ultima_busca: string | null
  cod_categoria: string | null
  desc_territorial: string | null
  num_vidas: number | null
  convenios_qt: number | null
  num_processo_mte?: string | null
  meio_recebimento?: string | null
  status_validacao?: string | null
  observacao_validacao?: string | null
}

interface CctBusca {
  id: number
  created_at: string
  cct_convencao_id?: number | null
  resposta?: string | null
  status: string | null
  link_documento: string | null
  link_download: string | null
  vigencia_inicio: string | null
  vigencia_fim: string | null
  partes: string | null
  codigo: string | null
  data_importacao?: string | null
  id_scgp?: number | null
  clausulas_especificas?: string | null
  scgp_id?: number | null
}

type SortKey =
  | 'id'
  | 'created_at'
  | 'cnpj_entidade_laboral'
  | 'cnpj_entidade_patronal'
  | 'id_scgp'
  | 'num_processo_mte'
  | 'data_inicio_vigencia'
  | 'data_fim_vigencia'
  | 'status'
  | 'status_mte'
  | 'data_ultima_busca'
  | 'cod_categoria'
  | 'desc_territorial'
  | 'num_vidas'
  | 'convenios_qt'

/** Presença do Nº de registro no MTE na listagem filtrada */
type FiltroNumeroMtePresenca = 'qualquer' | 'sem' | 'com'

const WEBHOOK_BUSCAR_CCT_MTE = 'https://webhook.pasiai.com/webhook/BuscaCCTMTE'
const WEBHOOK_INSERIR_CCT_SCGP = 'https://webhook.pasiai.com/webhook/InserirCCTSCGP'

const CCTSDashboardScgp = () => {
  const [convencoesData, setConvencoesData] = useState<ConvencaoData[]>([])
  const [categoriasData, setCategoriasData] = useState<CategoriaData[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [refreshError, setRefreshError] = useState<string | null>(null)
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false)
  const [selectedConvencao, setSelectedConvencao] = useState<ConvencaoData | null>(null)
  const [activeTab, setActiveTab] = useState<'dashboard' | 'relacaoAgrupada'>('dashboard')
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set())
  const [historicoBuscas, setHistoricoBuscas] = useState<Map<string, CctBusca[]>>(new Map())
  const [historicoBuscasLoading, setHistoricoBuscasLoading] = useState<Set<string>>(new Set())
  const [ultimaBuscaPorConvencao, setUltimaBuscaPorConvencao] = useState<Map<number, { status: string | null; created_at: string }>>(new Map())
  const [moduleTitle, setModuleTitle] = useState<string>('CCTs • Dashboard SCGP')
  const [moduleDescription, setModuleDescription] = useState<string>('Convenções SCGP – listagem e filtros')
  
  // Filtros
  const [filtrosExpandidos, setFiltrosExpandidos] = useState(true)
  const [filtroStatuses, setFiltroStatuses] = useState<string[]>(['EXPIRADA', 'VIGENTE'])
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false)
  const statusDropdownRef = useRef<HTMLDivElement>(null)
  const [filtroStatusMte, setFiltroStatusMte] = useState<string[]>([])
  const [statusMteDropdownOpen, setStatusMteDropdownOpen] = useState(false)
  const statusMteDropdownRef = useRef<HTMLDivElement>(null)
  const [filtroCNPJLaboral, setFiltroCNPJLaboral] = useState('')
  const [filtroCNPJPatronal, setFiltroCNPJPatronal] = useState('')
  const [filtroCategorias, setFiltroCategorias] = useState<string[]>([])
  const [categoriaDropdownOpen, setCategoriaDropdownOpen] = useState(false)
  const categoriaDropdownRef = useRef<HTMLDivElement>(null)
  const [filtroMeioRecebimento, setFiltroMeioRecebimento] = useState<string[]>([])
  const [meioRecebimentoDropdownOpen, setMeioRecebimentoDropdownOpen] = useState(false)
  const meioRecebimentoDropdownRef = useRef<HTMLDivElement>(null)
  const [filtroStatusValidacao, setFiltroStatusValidacao] = useState<string[]>([])
  const [statusValidacaoDropdownOpen, setStatusValidacaoDropdownOpen] = useState(false)
  const statusValidacaoDropdownRef = useRef<HTMLDivElement>(null)
  const [filtroDataInicioVigencia, setFiltroDataInicioVigencia] = useState('')
  const [filtroDataFimVigencia, setFiltroDataFimVigencia] = useState('')
  const [tipoFiltroDataInicio, setTipoFiltroDataInicio] = useState<'data' | 'mes' | 'ano'>('data')
  const [operadorFiltroInicioVigencia, setOperadorFiltroInicioVigencia] = useState<'igual' | 'a_partir_de'>('a_partir_de')
  const [tipoFiltroDataFim, setTipoFiltroDataFim] = useState<'data' | 'mes' | 'ano'>('data')
  const [operadorFiltroFimVigencia, setOperadorFiltroFimVigencia] = useState<'igual' | 'ate'>('ate')
  const [filtroIdScgp, setFiltroIdScgp] = useState('')
  const [filtroNumeroMte, setFiltroNumeroMte] = useState('')
  const [filtroConveniosQtMaiorQueZero, setFiltroConveniosQtMaiorQueZero] = useState(true)
  const [filtroNumeroMtePresenca, setFiltroNumeroMtePresenca] = useState<FiltroNumeroMtePresenca>('qualquer')
  const [filtroDataUltimaBusca, setFiltroDataUltimaBusca] = useState('')
  const [tipoFiltroDataUltimaBusca, setTipoFiltroDataUltimaBusca] = useState<'data' | 'mes' | 'ano' | 'hoje'>('data')
  const [operadorFiltroDataUltimaBusca, setOperadorFiltroDataUltimaBusca] = useState<'igual' | 'a_partir_de' | 'ate'>('igual')
  
  // Estados de ordenação
  const [sortBy, setSortBy] = useState<SortKey>('num_vidas')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  
  // Estados de paginação
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(20)

  // Modal de Busca MTE: confirmação → carregamento → ao fechar atualiza os dados
  const [isBuscaMteModalOpen, setIsBuscaMteModalOpen] = useState(false)
  const [buscaMteModalStatus, setBuscaMteModalStatus] = useState<'idle' | 'confirm' | 'loading' | 'success' | 'error'>('idle')
  const [buscaMteModalError, setBuscaMteModalError] = useState<string | null>(null)
  const [buscaMteModalConvencaoId, setBuscaMteModalConvencaoId] = useState<number | null>(null)
  const [buscaMteModalIdScgp, setBuscaMteModalIdScgp] = useState<number | null>(null)
  // Reposicionar na Relação Detalhada após fechar modal Busca MTE (groupKey do registro clicado)
  const [repositionGroupKeyAfterBuscaMte, setRepositionGroupKeyAfterBuscaMte] = useState<string | null>(null)
  // Busca no MTE em massa: seleção de convenções (apenas EXPIRADA)
  const [selectedForBuscaMteIds, setSelectedForBuscaMteIds] = useState<Set<number>>(new Set())
  // Modal Busca MTE em massa
  const [isBuscaMteMassaModalOpen, setIsBuscaMteMassaModalOpen] = useState(false)
  const [buscaMteMassaStatus, setBuscaMteMassaStatus] = useState<'idle' | 'confirm' | 'loading' | 'success' | 'error'>('idle')
  const [buscaMteMassaProgress, setBuscaMteMassaProgress] = useState<{ total: number; concluidos: number; erros: { id: number; message: string }[] }>({ total: 0, concluidos: 0, erros: [] })
  const buscaMteMassaPercentual = useMemo(() => {
    if (buscaMteMassaProgress.total <= 0) return 0
    return Math.min(100, Math.round((buscaMteMassaProgress.concluidos / buscaMteMassaProgress.total) * 100))
  }, [buscaMteMassaProgress.concluidos, buscaMteMassaProgress.total])
  // Modal Enviar para SCGP: confirmação → carregamento → ao fechar atualiza os dados
  const [isEnviarScgpModalOpen, setIsEnviarScgpModalOpen] = useState(false)
  const [enviarScgpModalStatus, setEnviarScgpModalStatus] = useState<'idle' | 'confirm' | 'loading' | 'success' | 'error'>('idle')
  const [enviarScgpModalError, setEnviarScgpModalError] = useState<string | null>(null)
  const [enviarScgpModalConvencaoId, setEnviarScgpModalConvencaoId] = useState<number | null>(null)
  const [enviarScgpModalIdBusca, setEnviarScgpModalIdBusca] = useState<number | null>(null)
  // Modal detalhes do registro do Histórico de buscas no MTE
  const [selectedBuscaHistorico, setSelectedBuscaHistorico] = useState<CctBusca | null>(null)
  const [isHistoricoBuscaModalOpen, setIsHistoricoBuscaModalOpen] = useState(false)
  const [historicoBuscaModalTab, setHistoricoBuscaModalTab] = useState<'dados' | 'resposta' | 'clausulas'>('dados')

  const { hasPermission, loading: permissionLoading } = usePermission('ccts_dashboardscgp', 'show')
  const { hasPermission: canInsert } = usePermission('ccts_dashboardscgp', 'insert')
  const { logAction } = useUserLogs()

  // Navegação horizontal (tabela)
  const tabelaScrollRef = useRef<HTMLDivElement | null>(null)
  const scrollTabelaBy = (dx: number) => {
    tabelaScrollRef.current?.scrollBy({ left: dx, behavior: 'smooth' })
  }
  const handleTabelaWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
      e.preventDefault()
      tabelaScrollRef.current?.scrollBy({ left: e.deltaY, behavior: 'auto' })
    }
  }

  // Buscar título e descrição do módulo (somente após permissão concedida)
  useEffect(() => {
    if (permissionLoading || !hasPermission) return
    const fetchModuleInfo = async () => {
      try {
        const result = await modulesService.getByName('ccts_dashboardscgp')
        if (result.success && result.data) {
          if (result.data.display_name) {
            setModuleTitle(result.data.display_name)
          }
          if (result.data.description) {
            setModuleDescription(result.data.description)
          }
        }
      } catch (err: any) {
        console.error('Erro ao buscar informações do módulo:', err.message)
      }
    }
    fetchModuleInfo()
  }, [permissionLoading, hasPermission])

  // Buscar categorias (somente após permissão concedida)
  useEffect(() => {
    if (permissionLoading || !hasPermission) return
    const fetchCategorias = async () => {
      try {
        const { data, error } = await supabase
          .from('cct_categorias')
          .select('*')
          .order('nome', { ascending: true })

        if (error) {
          throw error
        }

        setCategoriasData((data as CategoriaData[]) || [])
      } catch (err: any) {
        console.error('Erro ao carregar categorias:', err.message)
      }
    }
    fetchCategorias()
  }, [permissionLoading, hasPermission])

  // Buscar convenções — em lotes para garantir todos os registros (Supabase limita 1000 por consulta). Reutilizado no fechamento do modal Busca MTE.
  const fetchConvencoes = useCallback(async (options?: { silent?: boolean }) => {
    const silent = Boolean(options?.silent)
    try {
      if (silent) {
        setRefreshError(null)
        setIsRefreshing(true)
      } else {
        setLoading(true)
      }
      const batchSize = 1000
      let hasMore = true
      const allData: ConvencaoData[] = []
      let cursorCreatedAt: string | null = null
      let cursorId: number | null = null

      const convencoesColumns = 'id, created_at, cnpj_entidade_laboral, cnpj_entidade_patronal, id_scgp, num_processo_mte, data_inicio_vigencia, data_fim_vigencia, status, status_mte, data_ultima_busca, cod_categoria, desc_territorial, num_vidas, convenios_qt, meio_recebimento, status_validacao, observacao_validacao'
      while (hasMore) {
        let query = supabase
          .from('cct_convencoes')
          .select(convencoesColumns)
          // Ordenação determinística para evitar "pulos" com created_at empatado.
          .order('created_at', { ascending: false })
          .order('id', { ascending: false })
          .limit(batchSize)

        // Keyset pagination: busca próximos registros "abaixo" do cursor (created_at, id)
        if (cursorCreatedAt != null && cursorId != null) {
          // (created_at < cursorCreatedAt) OR (created_at = cursorCreatedAt AND id < cursorId)
          query = query.or(`created_at.lt.${cursorCreatedAt},and(created_at.eq.${cursorCreatedAt},id.lt.${cursorId})`)
        }

        const { data, error } = await query

        if (error) {
          throw error
        }

        // Supabase pode retornar int8 como string dependendo do client/config.
        // Normalizamos aqui para garantir `id_scgp` como number | null no app.
        const batch = (((data as any[]) || []).map((row) => ({
          ...row,
          id_scgp: row?.id_scgp != null ? Number(row.id_scgp) : null
        })) as ConvencaoData[])
        if (batch.length > 0) {
          allData.push(...batch)
          if (batch.length < batchSize) {
            hasMore = false
          } else {
            const last = batch[batch.length - 1]
            cursorCreatedAt = last?.created_at ?? null
            cursorId = last?.id ?? null
          }
        } else {
          hasMore = false
        }
      }

      setConvencoesData(allData)
    } catch (err: any) {
      const msg = err?.message ?? 'Erro ao carregar convenções'
      if (silent) {
        setRefreshError(msg)
      } else {
        setError(msg)
      }
    } finally {
      if (silent) {
        setIsRefreshing(false)
      } else {
        setLoading(false)
      }
    }
  }, [])

  useEffect(() => {
    if (permissionLoading || !hasPermission) return
    fetchConvencoes()
  }, [permissionLoading, hasPermission, fetchConvencoes])

  // Buscar último registro do histórico de buscas no MTE (registro mais recente de cct_buscas) por convenção
  useEffect(() => {
    if (!hasPermission) {
      setUltimaBuscaPorConvencao(new Map())
      return
    }

    if (!convencoesData || convencoesData.length === 0) {
      setUltimaBuscaPorConvencao(new Map())
      return
    }

    let isCancelled = false

    const fetchUltimasBuscas = async () => {
      try {
        const ids = Array.from(
          new Set(
            (convencoesData || [])
              .map(item => item.id)
              .filter((id): id is number => id != null)
          )
        )

        if (ids.length === 0) {
          if (!isCancelled) {
            setUltimaBuscaPorConvencao(new Map())
          }
          return
        }

        const batchSize = 1000
        const map = new Map<number, { status: string | null; created_at: string }>()

        for (let i = 0; i < ids.length; i += batchSize) {
          const batchIds = ids.slice(i, i + batchSize)
          const { data, error } = await supabase.rpc('rpc_cct_ultima_busca_por_convencao', {
            p_convencao_ids: batchIds
          })

          if (error) {
            throw error
          }

          ;((data as any[]) || []).forEach((row) => {
            const convId = row?.cct_convencao_id as number | null
            if (convId == null) return
            map.set(convId, {
              status: row?.status ?? null,
              created_at: row?.created_at
            })
          })
        }

        if (!isCancelled) {
          setUltimaBuscaPorConvencao(map)
        }
      } catch (err: any) {
        console.error('Erro ao carregar último status do histórico de buscas:', err?.message ?? err)
      }
    }

    fetchUltimasBuscas()

    return () => {
      isCancelled = true
    }
  }, [hasPermission, convencoesData])

  // Mapa de categorias por ID
  const categoriasMap = useMemo(() => {
    const map = new Map<string, string>()
    categoriasData.forEach(cat => {
      if (cat.id != null) {
        map.set(String(cat.id), cat.nome || '—')
      }
    })
    return map
  }, [categoriasData])

  // Obter nome da categoria
  const getNomeCategoria = (codCategoria: string | null): string => {
    if (!codCategoria) return '—'
    return categoriasMap.get(codCategoria) || '—'
  }

  // Truncar texto para exibição na tabela
  const truncateText = (text: string, maxLength: number): string => {
    if (!text || text.length <= maxLength) return text
    return text.substring(0, maxLength) + '...'
  }

  // Valores únicos de status para o select
  const statusDisponiveis = useMemo(() => {
    const statuses = convencoesData
      .map(item => item.status)
      .filter((status): status is string => status != null && status.trim() !== '')
    return Array.from(new Set(statuses)).sort()
  }, [convencoesData])

  const statusMteDisponiveis = useMemo(() => {
    const valores = convencoesData.map(item => (item.status_mte != null && item.status_mte.trim() !== '' ? item.status_mte : '—'))
    const unicos = Array.from(new Set(valores)).sort()
    return unicos
  }, [convencoesData])

  const meioRecebimentoDisponiveis = useMemo(() => {
    const valores = convencoesData
      .map(item => item.meio_recebimento)
      .filter((valor): valor is string => valor != null && valor.trim() !== '')
    return Array.from(new Set(valores)).sort()
  }, [convencoesData])

  const statusValidacaoDisponiveis = useMemo(() => {
    const valores = convencoesData
      .map(item => item.status_validacao)
      .filter((valor): valor is string => valor != null && valor.trim() !== '')
    return Array.from(new Set(valores)).sort()
  }, [convencoesData])

  // Lista com os mesmos filtros de convencoesFiltradas EXCETO status — usada apenas para Total de Convenções (distinct id_scgp)
  const convencoesParaTotalConvencoes = useMemo(() => {
    return convencoesData.filter(item => {
      const matchCNPJLaboral = !filtroCNPJLaboral || (item.cnpj_entidade_laboral || '').toLowerCase().includes(filtroCNPJLaboral.toLowerCase())
      const matchCNPJPatronal = !filtroCNPJPatronal || (item.cnpj_entidade_patronal || '').toLowerCase().includes(filtroCNPJPatronal.toLowerCase())
      const itemCodCategoria = item.cod_categoria != null ? String(item.cod_categoria) : ''
      const matchCategoria = filtroCategorias.length === 0 || (itemCodCategoria !== '' && filtroCategorias.includes(itemCodCategoria))
      const itemMeioRecebimento = (item.meio_recebimento || '').trim()
      const matchMeioRecebimento = filtroMeioRecebimento.length === 0 || filtroMeioRecebimento.some(f => f.trim().toLowerCase() === itemMeioRecebimento.toLowerCase())
      const itemStatusValidacao = (item.status_validacao || '').trim()
      const matchStatusValidacao = filtroStatusValidacao.length === 0 || filtroStatusValidacao.some(f => f.trim().toLowerCase() === itemStatusValidacao.toLowerCase())
      const matchIdScgp = !filtroIdScgp || String(item.id_scgp ?? '').toLowerCase().includes(filtroIdScgp.toLowerCase())
      const filtroNumeroMteNormalizado = filtroNumeroMte.replace(/\\D/g, '')
      const itemNumeroMteNormalizado = String(item.num_processo_mte ?? '').replace(/\\D/g, '')
      const matchNumeroMte = !filtroNumeroMteNormalizado || (itemNumeroMteNormalizado !== '' && itemNumeroMteNormalizado.includes(filtroNumeroMteNormalizado))

      let matchDataInicio = true
      let matchDataFim = true

      if (filtroDataInicioVigencia && item.data_inicio_vigencia) {
        const dataInicio = new Date(item.data_inicio_vigencia)
        const filtroStr = filtroDataInicioVigencia.trim()
        const isIgual = operadorFiltroInicioVigencia === 'igual'
        if (filtroStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
          const filtroInicio = new Date(filtroStr + 'T00:00:00')
          const filtroFim = new Date(filtroStr + 'T23:59:59')
          matchDataInicio = isIgual
            ? dataInicio.getTime() >= filtroInicio.getTime() && dataInicio.getTime() <= filtroFim.getTime()
            : dataInicio.getTime() >= filtroInicio.getTime()
        } else if (filtroStr.match(/^\d{4}-\d{2}$/)) {
          const [ano, mes] = filtroStr.split('-')
          const anoItem = dataInicio.getFullYear()
          const mesItem = dataInicio.getMonth() + 1
          matchDataInicio = isIgual
            ? anoItem === parseInt(ano) && mesItem === parseInt(mes)
            : anoItem > parseInt(ano) || (anoItem === parseInt(ano) && mesItem >= parseInt(mes))
        } else if (filtroStr.match(/^\d{4}$/)) {
          const anoItem = dataInicio.getFullYear()
          matchDataInicio = isIgual ? anoItem === parseInt(filtroStr) : anoItem >= parseInt(filtroStr)
        }
      }

      if (filtroDataFimVigencia && item.data_fim_vigencia) {
        const dataFim = new Date(item.data_fim_vigencia)
        const filtroStr = filtroDataFimVigencia.trim()
        const isIgual = operadorFiltroFimVigencia === 'igual'
        if (filtroStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
          const filtroInicio = new Date(filtroStr + 'T00:00:00')
          const filtroFim = new Date(filtroStr + 'T23:59:59')
          matchDataFim = isIgual
            ? dataFim.getTime() >= filtroInicio.getTime() && dataFim.getTime() <= filtroFim.getTime()
            : dataFim.getTime() <= filtroFim.getTime()
        } else if (filtroStr.match(/^\d{4}-\d{2}$/)) {
          const [ano, mes] = filtroStr.split('-')
          const anoItem = dataFim.getFullYear()
          const mesItem = dataFim.getMonth() + 1
          matchDataFim = isIgual
            ? anoItem === parseInt(ano) && mesItem === parseInt(mes)
            : anoItem < parseInt(ano) || (anoItem === parseInt(ano) && mesItem <= parseInt(mes))
        } else if (filtroStr.match(/^\d{4}$/)) {
          const anoItem = dataFim.getFullYear()
          matchDataFim = isIgual ? anoItem === parseInt(filtroStr) : anoItem <= parseInt(filtroStr)
        }
      }

      const matchConveniosQt = !filtroConveniosQtMaiorQueZero || (item.convenios_qt != null && item.convenios_qt > 0)
      const matchNumeroMtePresenca = (() => {
        const v = String(item.num_processo_mte ?? '').trim()
        if (filtroNumeroMtePresenca === 'sem') return v === ''
        if (filtroNumeroMtePresenca === 'com') return v !== ''
        return true
      })()

      let matchDataUltimaBusca = true
      if (filtroDataUltimaBusca && item.data_ultima_busca) {
        const raw = String(item.data_ultima_busca).trim()
        const dataStr = raw.match(/^\d{4}-\d{2}-\d{2}(T|$)/) ? raw.slice(0, 10) : (() => {
          const d = new Date(item.data_ultima_busca!)
          return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
        })()
        const filtroStr = filtroDataUltimaBusca.trim()
        const isIgual = operadorFiltroDataUltimaBusca === 'igual'
        const isAte = operadorFiltroDataUltimaBusca === 'ate'
        if (filtroStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
          matchDataUltimaBusca = isIgual
            ? dataStr === filtroStr
            : isAte
              ? dataStr <= filtroStr
              : dataStr >= filtroStr
        } else if (filtroStr.match(/^\d{4}-\d{2}$/)) {
          const [ano, mes] = filtroStr.split('-')
          const anoItem = parseInt(dataStr.slice(0, 4))
          const mesItem = parseInt(dataStr.slice(5, 7))
          matchDataUltimaBusca = isIgual
            ? anoItem === parseInt(ano) && mesItem === parseInt(mes)
            : isAte
              ? anoItem < parseInt(ano) || (anoItem === parseInt(ano) && mesItem <= parseInt(mes))
              : anoItem > parseInt(ano) || (anoItem === parseInt(ano) && mesItem >= parseInt(mes))
        } else if (filtroStr.match(/^\d{4}$/)) {
          const anoItem = parseInt(dataStr.slice(0, 4))
          matchDataUltimaBusca = isIgual
            ? anoItem === parseInt(filtroStr)
            : isAte
              ? anoItem <= parseInt(filtroStr)
              : anoItem >= parseInt(filtroStr)
        }
      } else if (filtroDataUltimaBusca && !item.data_ultima_busca) {
        matchDataUltimaBusca = false
      }
      
      return matchCNPJLaboral && matchCNPJPatronal && matchCategoria && matchMeioRecebimento && matchStatusValidacao && matchIdScgp && matchNumeroMte && matchDataInicio && matchDataFim && matchDataUltimaBusca && matchConveniosQt && matchNumeroMtePresenca
    })
  }, [convencoesData, filtroCNPJLaboral, filtroCNPJPatronal, filtroCategorias, filtroMeioRecebimento, filtroStatusValidacao, filtroIdScgp, filtroNumeroMte, filtroDataInicioVigencia, operadorFiltroInicioVigencia, filtroDataFimVigencia, operadorFiltroFimVigencia, filtroDataUltimaBusca, operadorFiltroDataUltimaBusca, filtroConveniosQtMaiorQueZero, filtroNumeroMtePresenca])

  const convencoesFiltradas = useMemo(() => {
    const filtered = convencoesData.filter(item => {
      const itemStatus = (item.status || '').trim().toLowerCase()
      const matchStatus = filtroStatuses.length === 0 || filtroStatuses.some(s => s.trim().toLowerCase() === itemStatus)
      const itemStatusMte = (item.status_mte != null && item.status_mte.trim() !== '') ? item.status_mte : '—'
      const matchStatusMte = filtroStatusMte.length === 0 || filtroStatusMte.some(s => s === itemStatusMte)
      const matchCNPJLaboral = !filtroCNPJLaboral || (item.cnpj_entidade_laboral || '').toLowerCase().includes(filtroCNPJLaboral.toLowerCase())
      const matchCNPJPatronal = !filtroCNPJPatronal || (item.cnpj_entidade_patronal || '').toLowerCase().includes(filtroCNPJPatronal.toLowerCase())
      const itemCodCategoria = item.cod_categoria != null ? String(item.cod_categoria) : ''
      const matchCategoria = filtroCategorias.length === 0 || (itemCodCategoria !== '' && filtroCategorias.includes(itemCodCategoria))
      const itemMeioRecebimento = (item.meio_recebimento || '').trim()
      const matchMeioRecebimento = filtroMeioRecebimento.length === 0 || filtroMeioRecebimento.some(f => f.trim().toLowerCase() === itemMeioRecebimento.toLowerCase())
      const itemStatusValidacao = (item.status_validacao || '').trim()
      const matchStatusValidacao = filtroStatusValidacao.length === 0 || filtroStatusValidacao.some(f => f.trim().toLowerCase() === itemStatusValidacao.toLowerCase())
      const matchIdScgp = !filtroIdScgp || String(item.id_scgp ?? '').toLowerCase().includes(filtroIdScgp.toLowerCase())
      const filtroNumeroMteNormalizado = filtroNumeroMte.replace(/\\D/g, '')
      const itemNumeroMteNormalizado = String(item.num_processo_mte ?? '').replace(/\\D/g, '')
      const matchNumeroMte = !filtroNumeroMteNormalizado || (itemNumeroMteNormalizado !== '' && itemNumeroMteNormalizado.includes(filtroNumeroMteNormalizado))
      const matchConveniosQt = !filtroConveniosQtMaiorQueZero || (item.convenios_qt != null && item.convenios_qt > 0)
      const matchNumeroMtePresenca = (() => {
        const v = String(item.num_processo_mte ?? '').trim()
        if (filtroNumeroMtePresenca === 'sem') return v === ''
        if (filtroNumeroMtePresenca === 'com') return v !== ''
        return true
      })()
      
      // Filtros de data (suporta data completa, mês ou ano)
      let matchDataInicio = true
      let matchDataFim = true
      let matchDataUltimaBusca = true

      if (filtroDataUltimaBusca && item.data_ultima_busca) {
        const raw = String(item.data_ultima_busca).trim()
        const dataStr = raw.match(/^\d{4}-\d{2}-\d{2}(T|$)/) ? raw.slice(0, 10) : (() => {
          const d = new Date(item.data_ultima_busca!)
          return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
        })()
        const filtroStr = filtroDataUltimaBusca.trim()
        const isIgual = operadorFiltroDataUltimaBusca === 'igual'
        const isAte = operadorFiltroDataUltimaBusca === 'ate'
        if (filtroStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
          matchDataUltimaBusca = isIgual
            ? dataStr === filtroStr
            : isAte
              ? dataStr <= filtroStr
              : dataStr >= filtroStr
        } else if (filtroStr.match(/^\d{4}-\d{2}$/)) {
          const [ano, mes] = filtroStr.split('-')
          const anoItem = parseInt(dataStr.slice(0, 4))
          const mesItem = parseInt(dataStr.slice(5, 7))
          matchDataUltimaBusca = isIgual
            ? anoItem === parseInt(ano) && mesItem === parseInt(mes)
            : isAte
              ? anoItem < parseInt(ano) || (anoItem === parseInt(ano) && mesItem <= parseInt(mes))
              : anoItem > parseInt(ano) || (anoItem === parseInt(ano) && mesItem >= parseInt(mes))
        } else if (filtroStr.match(/^\d{4}$/)) {
          const anoItem = parseInt(dataStr.slice(0, 4))
          matchDataUltimaBusca = isIgual
            ? anoItem === parseInt(filtroStr)
            : isAte
              ? anoItem <= parseInt(filtroStr)
              : anoItem >= parseInt(filtroStr)
        }
      } else if (filtroDataUltimaBusca && !item.data_ultima_busca) {
        matchDataUltimaBusca = false
      }
      
      if (filtroDataInicioVigencia && item.data_inicio_vigencia) {
        const dataInicio = new Date(item.data_inicio_vigencia)
        const filtroStr = filtroDataInicioVigencia.trim()
        const isIgual = operadorFiltroInicioVigencia === 'igual'
        
        // Detectar formato: YYYY-MM-DD, YYYY-MM ou YYYY
        if (filtroStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
          // Data completa: YYYY-MM-DD
          const filtroInicio = new Date(filtroStr + 'T00:00:00')
          const filtroFim = new Date(filtroStr + 'T23:59:59')
          if (isIgual) {
            matchDataInicio = dataInicio.getTime() >= filtroInicio.getTime() && dataInicio.getTime() <= filtroFim.getTime()
          } else {
            matchDataInicio = dataInicio.getTime() >= filtroInicio.getTime()
          }
        } else if (filtroStr.match(/^\d{4}-\d{2}$/)) {
          // Somente mês: YYYY-MM
          const [ano, mes] = filtroStr.split('-')
          const anoItem = dataInicio.getFullYear()
          const mesItem = dataInicio.getMonth() + 1
          if (isIgual) {
            matchDataInicio = anoItem === parseInt(ano) && mesItem === parseInt(mes)
          } else {
            matchDataInicio = anoItem > parseInt(ano) || (anoItem === parseInt(ano) && mesItem >= parseInt(mes))
          }
        } else if (filtroStr.match(/^\d{4}$/)) {
          // Somente ano: YYYY
          const anoItem = dataInicio.getFullYear()
          if (isIgual) {
            matchDataInicio = anoItem === parseInt(filtroStr)
          } else {
            matchDataInicio = anoItem >= parseInt(filtroStr)
          }
        }
      }
      
      if (filtroDataFimVigencia && item.data_fim_vigencia) {
        const dataFim = new Date(item.data_fim_vigencia)
        const filtroStr = filtroDataFimVigencia.trim()
        const isIgual = operadorFiltroFimVigencia === 'igual'
        
        // Detectar formato: YYYY-MM-DD, YYYY-MM ou YYYY
        if (filtroStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
          // Data completa: YYYY-MM-DD
          const filtroInicio = new Date(filtroStr + 'T00:00:00')
          const filtroFim = new Date(filtroStr + 'T23:59:59')
          if (isIgual) {
            matchDataFim = dataFim.getTime() >= filtroInicio.getTime() && dataFim.getTime() <= filtroFim.getTime()
          } else {
            matchDataFim = dataFim.getTime() <= filtroFim.getTime()
          }
        } else if (filtroStr.match(/^\d{4}-\d{2}$/)) {
          // Somente mês: YYYY-MM
          const [ano, mes] = filtroStr.split('-')
          const anoItem = dataFim.getFullYear()
          const mesItem = dataFim.getMonth() + 1
          if (isIgual) {
            matchDataFim = anoItem === parseInt(ano) && mesItem === parseInt(mes)
          } else {
            matchDataFim = anoItem < parseInt(ano) || (anoItem === parseInt(ano) && mesItem <= parseInt(mes))
          }
        } else if (filtroStr.match(/^\d{4}$/)) {
          // Somente ano: YYYY
          const anoItem = dataFim.getFullYear()
          if (isIgual) {
            matchDataFim = anoItem === parseInt(filtroStr)
          } else {
            matchDataFim = anoItem <= parseInt(filtroStr)
          }
        }
      }
      
      return matchStatus && matchStatusMte && matchCNPJLaboral && matchCNPJPatronal && matchCategoria && matchMeioRecebimento && matchStatusValidacao && matchIdScgp && matchNumeroMte && matchDataInicio && matchDataFim && matchDataUltimaBusca && matchConveniosQt && matchNumeroMtePresenca
    })

    // Aplicar ordenação
    return filtered.sort((a, b) => {
      const getSortValue = (item: ConvencaoData, key: SortKey): any => {
        switch (key) {
          case 'id':
            return item.id
          case 'created_at':
            return item.created_at ? new Date(item.created_at).getTime() : -Infinity
          case 'cnpj_entidade_laboral':
            return (item.cnpj_entidade_laboral || '').toLowerCase()
          case 'cnpj_entidade_patronal':
            return (item.cnpj_entidade_patronal || '').toLowerCase()
          case 'id_scgp':
            return item.id_scgp ?? -Infinity
          case 'num_processo_mte':
            return (item.num_processo_mte || '').toLowerCase()
          case 'data_inicio_vigencia':
            return item.data_inicio_vigencia ? new Date(item.data_inicio_vigencia).getTime() : -Infinity
          case 'data_fim_vigencia':
            return item.data_fim_vigencia ? new Date(item.data_fim_vigencia).getTime() : -Infinity
          case 'status':
            return (item.status || '').toLowerCase()
          case 'status_mte':
            return ((item.status_mte != null && item.status_mte.trim() !== '') ? item.status_mte : '—').toLowerCase()
          case 'data_ultima_busca':
            return item.data_ultima_busca ? new Date(item.data_ultima_busca).getTime() : -Infinity
          case 'cod_categoria':
            return (item.cod_categoria || '').toLowerCase()
          case 'desc_territorial':
            return (item.desc_territorial || '').toLowerCase()
          case 'num_vidas':
            return item.num_vidas ?? -Infinity
          case 'convenios_qt':
            return item.convenios_qt ?? -Infinity
          default:
            return ''
        }
      }

      const aValue = getSortValue(a, sortBy)
      const bValue = getSortValue(b, sortBy)

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1
      return 0
    })
  }, [convencoesData, filtroStatuses, filtroStatusMte, filtroCNPJLaboral, filtroCNPJPatronal, filtroCategorias, filtroMeioRecebimento, filtroStatusValidacao, filtroDataInicioVigencia, operadorFiltroInicioVigencia, filtroDataFimVigencia, operadorFiltroFimVigencia, filtroDataUltimaBusca, operadorFiltroDataUltimaBusca, filtroIdScgp, filtroNumeroMte, filtroConveniosQtMaiorQueZero, filtroNumeroMtePresenca, sortBy, sortOrder])

  const idsExpiradasFiltradas = useMemo(
    () =>
      convencoesFiltradas
        .filter((item) => (item.status || '').toUpperCase() === 'EXPIRADA')
        .map((item) => item.id),
    [convencoesFiltradas]
  )

  // Resetar página quando filtros mudarem ou quando trocar de aba
  useEffect(() => {
    setCurrentPage(1)
  }, [filtroStatuses, filtroStatusMte, filtroCNPJLaboral, filtroCNPJPatronal, filtroCategorias, filtroMeioRecebimento, filtroStatusValidacao, filtroDataInicioVigencia, operadorFiltroInicioVigencia, filtroDataFimVigencia, operadorFiltroFimVigencia, filtroDataUltimaBusca, operadorFiltroDataUltimaBusca, filtroIdScgp, filtroNumeroMte, filtroConveniosQtMaiorQueZero, filtroNumeroMtePresenca, activeTab])

  // Fechar dropdown de status ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target as Node)) {
        setStatusDropdownOpen(false)
      }
    }
    if (statusDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [statusDropdownOpen])

  // Fechar dropdown de Busca MTE ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (statusMteDropdownRef.current && !statusMteDropdownRef.current.contains(event.target as Node)) {
        setStatusMteDropdownOpen(false)
      }
    }
    if (statusMteDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [statusMteDropdownOpen])

  // Fechar dropdown de Meio de Recebimento ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (meioRecebimentoDropdownRef.current && !meioRecebimentoDropdownRef.current.contains(event.target as Node)) {
        setMeioRecebimentoDropdownOpen(false)
      }
    }
    if (meioRecebimentoDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [meioRecebimentoDropdownOpen])

  // Fechar dropdown de Status de Validação ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (statusValidacaoDropdownRef.current && !statusValidacaoDropdownRef.current.contains(event.target as Node)) {
        setStatusValidacaoDropdownOpen(false)
      }
    }
    if (statusValidacaoDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [statusValidacaoDropdownOpen])

  // Fechar dropdown de Categoria ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (categoriaDropdownRef.current && !categoriaDropdownRef.current.contains(event.target as Node)) {
        setCategoriaDropdownOpen(false)
      }
    }
    if (categoriaDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [categoriaDropdownOpen])

  // Dados agrupados para a aba Relação Detalhada
  const convencoesAgrupadas = useMemo(() => {
    // Criar uma chave única para cada combinação dos campos de agrupamento
    const gruposMap = new Map<string, ConvencaoData & { quantidade: number; registros: ConvencaoData[] }>()
    
    convencoesFiltradas.forEach(item => {
      // Criar chave única baseada nos campos de agrupamento
      const chave = [
        item.created_at || '',
        String(item.id_scgp ?? ''),
        item.data_inicio_vigencia || '',
        item.data_fim_vigencia || '',
        item.status || '',
        item.cod_categoria || '',
        item.desc_territorial || '',
        item.num_vidas?.toString() || '',
        item.convenios_qt?.toString() || ''
      ].join('|')
      
      if (gruposMap.has(chave)) {
        // Incrementar contador e adicionar registro à lista
        const grupo = gruposMap.get(chave)!
        grupo.quantidade = (grupo.quantidade || 1) + 1
        grupo.registros.push(item)
      } else {
        // Criar novo grupo com o primeiro registro
        gruposMap.set(chave, {
          ...item,
          quantidade: 1,
          registros: [item]
        })
      }
    })
    
    // Converter map para array e ordenar
    const grupos = Array.from(gruposMap.values())
    
    // Aplicar ordenação
    return grupos.sort((a, b) => {
      const getSortValue = (item: ConvencaoData & { quantidade: number }, key: SortKey): any => {
        switch (key) {
          case 'created_at':
            return item.created_at ? new Date(item.created_at).getTime() : -Infinity
          case 'id_scgp':
            return item.id_scgp ?? -Infinity
          case 'num_processo_mte':
            return (item.num_processo_mte || '').toLowerCase()
          case 'data_inicio_vigencia':
            return item.data_inicio_vigencia ? new Date(item.data_inicio_vigencia).getTime() : -Infinity
          case 'data_fim_vigencia':
            return item.data_fim_vigencia ? new Date(item.data_fim_vigencia).getTime() : -Infinity
          case 'status':
            return (item.status || '').toLowerCase()
          case 'status_mte':
            return ((item.status_mte != null && item.status_mte.trim() !== '') ? item.status_mte : '—').toLowerCase()
          case 'data_ultima_busca':
            return item.data_ultima_busca ? new Date(item.data_ultima_busca).getTime() : -Infinity
          case 'cod_categoria':
            return (item.cod_categoria || '').toLowerCase()
          case 'desc_territorial':
            return (item.desc_territorial || '').toLowerCase()
          case 'num_vidas':
            return item.num_vidas ?? -Infinity
          case 'convenios_qt':
            return item.convenios_qt ?? -Infinity
          default:
            return ''
        }
      }

      const aValue = getSortValue(a, sortBy)
      const bValue = getSortValue(b, sortBy)

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1
      return 0
    })
  }, [convencoesFiltradas, sortBy, sortOrder])

  // Buscar histórico de buscas (cct_buscas) ao expandir um grupo.
  // Vínculo: cct_convencoes.id = cct_buscas.cct_convencao_id
  useEffect(() => {
    const toFetch = Array.from(expandedGroups).filter(groupKey => !historicoBuscas.has(groupKey) && !historicoBuscasLoading.has(groupKey))
    if (toFetch.length === 0) return

    const fetchGroupBuscas = async (groupKey: string) => {
      const grupo = convencoesAgrupadas.find((g: ConvencaoData & { registros?: ConvencaoData[] }) => getGroupKey(g) === groupKey)
      const registros = grupo ? ((grupo as ConvencaoData & { registros: ConvencaoData[] }).registros || [grupo]) : []
      const ids = registros.map((r: ConvencaoData) => r.id).filter(Boolean)
      if (ids.length === 0) {
        setHistoricoBuscas(prev => new Map(prev).set(groupKey, []))
        return
      }
      setHistoricoBuscasLoading(prev => new Set(prev).add(groupKey))
      try {
        const { data, error } = await supabase
          .from('cct_buscas')
          .select('id, created_at, cct_convencao_id, resposta, status, link_documento, link_download, vigencia_inicio, vigencia_fim, partes, codigo, data_importacao, id_scgp, clausulas_especificas')
          .in('cct_convencao_id', ids)
          .order('created_at', { ascending: false })
        if (error) throw error
        const normalized: CctBusca[] = ((data as any[]) || []).map((row) => ({
          ...row,
          id_scgp: row?.id_scgp != null ? Number(row.id_scgp) : null
        }))
        setHistoricoBuscas(prev => new Map(prev).set(groupKey, normalized))
      } catch (err) {
        console.error('Erro ao carregar histórico de buscas:', err)
        setHistoricoBuscas(prev => new Map(prev).set(groupKey, []))
      } finally {
        setHistoricoBuscasLoading(prev => {
          const next = new Set(prev)
          next.delete(groupKey)
          return next
        })
      }
    }

    toFetch.forEach(groupKey => fetchGroupBuscas(groupKey))
  }, [expandedGroups, convencoesAgrupadas, historicoBuscas, historicoBuscasLoading])

  // Estatísticas para o Dashboard
  const dashboardStats = useMemo(() => {
    // Total de Convenções: distinct id_scgp considerando filtro de status
    const convencoesPorIdScgpParaTotal = new Map<number, ConvencaoData>()
    convencoesFiltradas.forEach(item => {
      if (item.id_scgp != null) {
        if (!convencoesPorIdScgpParaTotal.has(item.id_scgp)) {
          convencoesPorIdScgpParaTotal.set(item.id_scgp, item)
        }
      }
    })
    const total = convencoesPorIdScgpParaTotal.size

    // Total de Vidas e Total de Convenios: somam num_vidas/convenios_qt UMA VEZ por id_scgp distinto (mesma base do Total de Convenções, com filtro de status)
    const totalVidas = Array.from(convencoesPorIdScgpParaTotal.values()).reduce((sum, item) => sum + (item.num_vidas || 0), 0)
    const totalConvenios = Array.from(convencoesPorIdScgpParaTotal.values()).reduce((sum, item) => sum + (item.convenios_qt || 0), 0)

    // Demais métricas usam convenções filtradas (com status): distinct id_scgp
    const convencoesPorIdScgp = new Map<number, ConvencaoData>()
    convencoesFiltradas.forEach(item => {
      if (item.id_scgp != null) {
        if (!convencoesPorIdScgp.has(item.id_scgp)) {
          convencoesPorIdScgp.set(item.id_scgp, item)
        }
      }
    })

    // Total de CCTs Vigentes: mesma base (com filtro de status), filtrando somente status === 'VIGENTE'; distinct id_scgp
    const convencoesVigentesPorIdScgp = new Map<number, ConvencaoData>()
    convencoesFiltradas.forEach(item => {
      if (item.id_scgp != null && (item.status || '').toUpperCase() === 'VIGENTE') {
        if (!convencoesVigentesPorIdScgp.has(item.id_scgp)) {
          convencoesVigentesPorIdScgp.set(item.id_scgp, item)
        }
      }
    })
    const totalConvencoesVigentes = convencoesVigentesPorIdScgp.size
    const totalVidasVigentes = Array.from(convencoesVigentesPorIdScgp.values()).reduce((sum, item) => sum + (item.num_vidas || 0), 0)
    const totalConveniosVigentes = Array.from(convencoesVigentesPorIdScgp.values()).reduce((sum, item) => sum + (item.convenios_qt || 0), 0)

    // Total de CCTs Expiradas: mesma base (com filtro de status), filtrando somente status === 'EXPIRADA'; distinct id_scgp
    const convencoesExpiradasPorIdScgp = new Map<number, ConvencaoData>()
    convencoesFiltradas.forEach(item => {
      if (item.id_scgp != null && (item.status || '').toUpperCase() === 'EXPIRADA') {
        if (!convencoesExpiradasPorIdScgp.has(item.id_scgp)) {
          convencoesExpiradasPorIdScgp.set(item.id_scgp, item)
        }
      }
    })
    const totalConvencoesExpiradas = convencoesExpiradasPorIdScgp.size
    const totalVidasExpiradas = Array.from(convencoesExpiradasPorIdScgp.values()).reduce((sum, item) => sum + (item.num_vidas || 0), 0)
    const totalConveniosExpirados = Array.from(convencoesExpiradasPorIdScgp.values()).reduce((sum, item) => sum + (item.convenios_qt || 0), 0)

    // Distribuição por status usando DISTINCT id_scgp
    const statusDistribuicao = new Map<string, number>()
    Array.from(convencoesPorIdScgp.values()).forEach(item => {
      const status = item.status || 'Sem Status'
      statusDistribuicao.set(status, (statusDistribuicao.get(status) || 0) + 1)
    })
    const statusData = Array.from(statusDistribuicao.entries()).map(([name, value]) => ({ name, value }))

    // Distribuição por categoria - Top 10
    const categoriaDistribuicao = new Map<string, number>()
    convencoesFiltradas.forEach(item => {
      const categoriaNome = getNomeCategoria(item.cod_categoria)
      categoriaDistribuicao.set(categoriaNome, (categoriaDistribuicao.get(categoriaNome) || 0) + 1)
    })
    const categoriaData = Array.from(categoriaDistribuicao.entries())
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10)

    // Número de CCTs (distinct id_scgp) por mês/ano do fim de vigência
    const cctPorFimVigenciaMap = new Map<string, number>()
    Array.from(convencoesPorIdScgp.values()).forEach(item => {
      if (!item.data_fim_vigencia || item.data_fim_vigencia.trim() === '') {
        const prev = cctPorFimVigenciaMap.get('Sem data') || 0
        cctPorFimVigenciaMap.set('Sem data', prev + 1)
        return
      }
      const d = new Date(item.data_fim_vigencia)
      if (Number.isNaN(d.getTime())) {
        const prev = cctPorFimVigenciaMap.get('Sem data') || 0
        cctPorFimVigenciaMap.set('Sem data', prev + 1)
        return
      }
      const year = d.getFullYear()
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const key = `${year}-${month}` // YYYY-MM para ordenação
      cctPorFimVigenciaMap.set(key, (cctPorFimVigenciaMap.get(key) || 0) + 1)
    })
    const cctPorFimVigenciaData = Array.from(cctPorFimVigenciaMap.entries())
      .map(([key, value]) => ({
        name: key === 'Sem data' ? 'Sem data' : `${key.slice(6)}/${key.slice(0, 4)}`, // MM/YYYY para exibição
        value,
        _sortKey: key
      }))
      .sort((a, b) => {
        if (a._sortKey === 'Sem data') return 1
        if (b._sortKey === 'Sem data') return -1
        return a._sortKey.localeCompare(b._sortKey)
      })
      .map(({ name, value }) => ({ name, value }))

    // Indicadores de início e fim de vigência (distinct id_scgp a partir de convencoesPorIdScgpParaTotal)
    const hoje = new Date()
    hoje.setHours(23, 59, 59, 999)
    const ano = hoje.getFullYear()
    const mes = hoje.getMonth()
    // Início: somente mês anterior (inteiro) e somente dentro dos últimos 3 meses
    const inicioMesAnterior = new Date(ano, mes - 1, 1)
    inicioMesAnterior.setHours(0, 0, 0, 0)
    const fimMesAnterior = new Date(ano, mes, 0)
    fimMesAnterior.setHours(23, 59, 59, 999)
    const inicioTresMesesAtras = new Date(ano, mes - 3, 1)
    inicioTresMesesAtras.setHours(0, 0, 0, 0)
    // Fim: somente próximo mês (inteiro) e somente dentro dos próximos 3 meses
    const inicioProximoMes = new Date(ano, mes + 1, 1)
    inicioProximoMes.setHours(0, 0, 0, 0)
    const fimProximoMes = new Date(ano, mes + 2, 0)
    fimProximoMes.setHours(23, 59, 59, 999)
    const fimProximos3Meses = new Date(ano, mes + 4, 0)
    fimProximos3Meses.setHours(23, 59, 59, 999)

    const cctsInicioUltimoMes = new Map<number, ConvencaoData>()
    const cctsInicioUltimos3Meses = new Map<number, ConvencaoData>()
    const cctsVigentesAteProximoMes = new Map<number, ConvencaoData>()
    const cctsVigentesAteProximos3Meses = new Map<number, ConvencaoData>()

    convencoesParaTotalConvencoes.forEach(item => {
      if (item.id_scgp == null) return
      const id = item.id_scgp
      const isVigente = (item.status || '').toUpperCase() === 'VIGENTE'

      if (item.data_inicio_vigencia) {
        const dInicio = new Date(item.data_inicio_vigencia)
        if (!Number.isNaN(dInicio.getTime())) {
          if (dInicio >= inicioMesAnterior && dInicio <= fimMesAnterior && !cctsInicioUltimoMes.has(id)) cctsInicioUltimoMes.set(id, item)
          if (dInicio >= inicioTresMesesAtras && dInicio <= hoje && !cctsInicioUltimos3Meses.has(id)) cctsInicioUltimos3Meses.set(id, item)
        }
      }

      if (isVigente && item.data_fim_vigencia) {
        const dFim = new Date(item.data_fim_vigencia)
        if (!Number.isNaN(dFim.getTime())) {
          if (dFim >= inicioProximoMes && dFim <= fimProximoMes && !cctsVigentesAteProximoMes.has(id)) cctsVigentesAteProximoMes.set(id, item)
          if (dFim >= inicioProximoMes && dFim <= fimProximos3Meses && !cctsVigentesAteProximos3Meses.has(id)) cctsVigentesAteProximos3Meses.set(id, item)
        }
      }
    })

    const cctsInicioUltimoMesCount = cctsInicioUltimoMes.size
    const cctsInicioUltimos3MesesCount = cctsInicioUltimos3Meses.size
    const cctsVigentesAteProximoMesCount = cctsVigentesAteProximoMes.size
    const cctsVigentesAteProximos3MesesCount = cctsVigentesAteProximos3Meses.size
    const vidasInicioUltimoMes = Array.from(cctsInicioUltimoMes.values()).reduce((s, i) => s + (i.num_vidas || 0), 0)
    const vidasInicioUltimos3Meses = Array.from(cctsInicioUltimos3Meses.values()).reduce((s, i) => s + (i.num_vidas || 0), 0)
    const vidasVigentesAteProximoMes = Array.from(cctsVigentesAteProximoMes.values()).reduce((s, i) => s + (i.num_vidas || 0), 0)
    const vidasVigentesAteProximos3Meses = Array.from(cctsVigentesAteProximos3Meses.values()).reduce((s, i) => s + (i.num_vidas || 0), 0)

    return {
      total,
      totalConvencoesVigentes,
      totalConvencoesExpiradas,
      totalVidas,
      totalVidasVigentes,
      totalVidasExpiradas,
      totalConvenios,
      totalConveniosVigentes,
      totalConveniosExpirados,
      statusData,
      categoriaData,
      cctPorFimVigenciaData,
      cctsInicioUltimoMesCount,
      cctsInicioUltimos3MesesCount,
      cctsVigentesAteProximoMesCount,
      cctsVigentesAteProximos3MesesCount,
      vidasInicioUltimoMes,
      vidasInicioUltimos3Meses,
      vidasVigentesAteProximoMes,
      vidasVigentesAteProximos3Meses
    }
  }, [convencoesParaTotalConvencoes, convencoesFiltradas, getNomeCategoria])

  // Tooltip formatter para gráficos
  const pieTooltipFormatter = (value: number, name: string) => {
    return [`${value}`, name]
  }

  const pieLabelText = (entry: any) => {
    const pct = (((entry?.percent ?? 0) as number) * 100).toFixed(2)
    return `${entry?.value ?? 0} (${pct}%)`
  }

  const renderPieTooltip = (props: any) => {
    const { active, payload } = props
    if (!active || !payload || payload.length === 0) return null
    return (
      <div className="rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 shadow dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100">
        {payload.map((p: any) => {
          const value = Number(p?.value) || 0
          return (
            <div key={p?.name || p?.dataKey} className="whitespace-nowrap">
              {`${p?.name}: ${value}`}
            </div>
          )
        })}
      </div>
    )
  }

  // Calcular dados da paginação
  const totalPages = Math.ceil(convencoesFiltradas.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const convencoesPaginadas = convencoesFiltradas.slice(startIndex, endIndex)
  
  // Paginação para dados agrupados (para aba Relação Detalhada)
  const totalPagesAgrupadas = Math.ceil(convencoesAgrupadas.length / itemsPerPage)
  const startIndexAgrupadas = (currentPage - 1) * itemsPerPage
  const endIndexAgrupadas = startIndexAgrupadas + itemsPerPage
  const convencoesAgrupadasPaginadas = convencoesAgrupadas.slice(startIndexAgrupadas, endIndexAgrupadas)

  const toggleSort = (key: string) => {
    setExpandedGroups(new Set()) // Retrair registros expandidos ao ordenar
    if (sortBy === key) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(key as SortKey)
      setSortOrder('asc')
    }
  }

  // Handlers de paginação
  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1)
    }
  }

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1)
    }
  }

  const handleRowClick = (item: ConvencaoData) => {
    setSelectedConvencao(item)
    setIsDetailsModalOpen(true)
    logAction('view', 'other', null, `Visualizou detalhes da convenção SCGP (ID ${item.id}${item.id_scgp != null ? `, id_scgp: ${item.id_scgp}` : ''})`, {
      convencao_id: item.id,
      id_scgp: item.id_scgp ?? null,
      module: 'ccts_dashboardscgp'
    }).catch((e) => console.warn('Erro ao registrar log:', e))
  }

  const handleCloseDetailsModal = () => {
    setIsDetailsModalOpen(false)
    setSelectedConvencao(null)
  }

  // Função para criar chave única do grupo
  const getGroupKey = (item: ConvencaoData): string => {
    return [
      item.created_at || '',
      String(item.id_scgp ?? ''),
      item.data_inicio_vigencia || '',
      item.data_fim_vigencia || '',
      item.status || '',
      item.cod_categoria || '',
      item.desc_territorial || '',
      item.num_vidas?.toString() || '',
      item.convenios_qt?.toString() || ''
    ].join('|')
  }

  // Função para expandir/recolher grupo (acordeão: ao expandir um, retrai os demais)
  const toggleGroupExpand = (groupKey: string, e: React.MouseEvent) => {
    e.stopPropagation() // Prevenir que o clique expanda o modal
    setExpandedGroups(prev => {
      if (prev.has(groupKey)) {
        const newSet = new Set(prev)
        newSet.delete(groupKey)
        return newSet
      }
      // Ao expandir, manter apenas este grupo (retrair os demais)
      return new Set([groupKey])
    })
  }

  // Após refetch por fechamento do modal Busca MTE: reposicionar na Relação Detalhada no registro que foi clicado
  useEffect(() => {
    if (!repositionGroupKeyAfterBuscaMte || !convencoesAgrupadas?.length) return
    const keyToScroll = repositionGroupKeyAfterBuscaMte
    const idx = convencoesAgrupadas.findIndex((g: ConvencaoData & { registros?: ConvencaoData[] }) => getGroupKey(g) === keyToScroll)
    if (idx < 0) {
      setRepositionGroupKeyAfterBuscaMte(null)
      return
    }
    const page = Math.floor(idx / itemsPerPage) + 1
    setCurrentPage(page)
    setExpandedGroups(new Set([keyToScroll]))
    setActiveTab('relacaoAgrupada')
    setRepositionGroupKeyAfterBuscaMte(null)

    const t = setTimeout(() => {
      const rows = document.querySelectorAll('tr[data-group-key]')
      for (let i = 0; i < rows.length; i++) {
        if ((rows[i] as HTMLElement).getAttribute('data-group-key') === keyToScroll) {
          rows[i].scrollIntoView({ behavior: 'smooth', block: 'nearest' })
          break
        }
      }
    }, 250)
    return () => clearTimeout(t)
  }, [convencoesAgrupadas, repositionGroupKeyAfterBuscaMte, itemsPerPage])

  // Buscar CCT no MTE: abre modal de confirmação; ao confirmar inicia a busca. Se item for passado (Relação Detalhada), guarda groupKey para reposicionar após refetch.
  const handleBuscarCctMte = (id: number, item?: ConvencaoData) => {
    if (!canInsert) return
    setBuscaMteModalConvencaoId(id)
    setBuscaMteModalIdScgp(item?.id_scgp ?? null)
    setBuscaMteModalStatus('confirm')
    setBuscaMteModalError(null)
    if (item != null) setRepositionGroupKeyAfterBuscaMte(getGroupKey(item))
    setIsBuscaMteModalOpen(true)
  }

  const handleConfirmBuscaMte = async () => {
    const id = buscaMteModalConvencaoId
    if (id == null) return
    setBuscaMteModalStatus('loading')
    try {
      let url = `${WEBHOOK_BUSCAR_CCT_MTE}?id=${encodeURIComponent(id)}`
      if (buscaMteModalIdScgp != null) {
        url += `&id_scgp=${encodeURIComponent(buscaMteModalIdScgp)}`
      }
      const res = await fetch(url, { method: 'GET' })
      if (!res.ok) throw new Error(`Erro ${res.status}`)
      setBuscaMteModalStatus('success')
      logAction('other', 'other', null, `Acionou busca CCT no MTE para convenção ID ${id}`, {
        convencao_id: id,
        module: 'ccts_dashboardscgp',
        action_type: 'busca_mte'
      }).catch((e) => console.warn('Erro ao registrar log:', e))
    } catch (err: any) {
      console.error('Erro ao acionar Buscar CCT no MTE:', err)
      setBuscaMteModalError(err?.message ?? 'Não foi possível acionar a busca no MTE. Tente novamente.')
      setBuscaMteModalStatus('error')
    }
  }

  const handleCloseBuscaMteModal = () => {
    if (buscaMteModalStatus === 'success' || buscaMteModalStatus === 'error') {
      fetchConvencoes()
      // Invalidar histórico dos grupos expandidos para recarregar a exibição após a busca
      setHistoricoBuscas(prev => {
        const next = new Map(prev)
        expandedGroups.forEach(k => next.delete(k))
        return next
      })
      setHistoricoBuscasLoading(prev => {
        const next = new Set(prev)
        expandedGroups.forEach(k => next.delete(k))
        return next
      })
    }
    setIsBuscaMteModalOpen(false)
    setBuscaMteModalStatus('idle')
    setBuscaMteModalError(null)
    setBuscaMteModalConvencaoId(null)
    setBuscaMteModalIdScgp(null)
  }

  const handleCancelBuscaMteModal = () => {
    setIsBuscaMteModalOpen(false)
    setBuscaMteModalStatus('idle')
    setBuscaMteModalError(null)
    setBuscaMteModalConvencaoId(null)
    setBuscaMteModalIdScgp(null)
  }

  const handleConfirmBuscaMteMassa = async () => {
    if (!canInsert) return
    const ids = Array.from(selectedForBuscaMteIds)
    if (ids.length === 0) return
    setBuscaMteMassaStatus('loading')
    setBuscaMteMassaProgress({ total: ids.length, concluidos: 0, erros: [] })
    let concluidos = 0
    const erros: { id: number; message: string }[] = []
    // Criar um mapa de id -> id_scgp a partir dos dados disponíveis
    const idToIdScgpMap = new Map<number, number | null>()
    convencoesData.forEach(item => {
      if (ids.includes(item.id)) {
        idToIdScgpMap.set(item.id, item.id_scgp)
      }
    })
    for (let i = 0; i < ids.length; i++) {
      const id = ids[i]
      try {
        let url = `${WEBHOOK_BUSCAR_CCT_MTE}?id=${encodeURIComponent(id)}`
        const idScgp = idToIdScgpMap.get(id)
        if (idScgp != null) {
          url += `&id_scgp=${encodeURIComponent(idScgp)}`
        }
        const res = await fetch(url, { method: 'GET' })
        if (!res.ok) throw new Error(`Erro ${res.status}`)
        concluidos += 1
      } catch (err: any) {
        erros.push({ id, message: err?.message ?? 'Erro na requisição' })
      }
      setBuscaMteMassaProgress({ total: ids.length, concluidos, erros: [...erros] })
      if (i < ids.length - 1) {
        await new Promise((r) => setTimeout(r, 5000))
      }
    }
    setBuscaMteMassaStatus(erros.length === ids.length ? 'error' : 'success')
    logAction('other', 'other', null, `Acionou busca CCT no MTE em massa: ${concluidos} de ${ids.length} convenções${erros.length > 0 ? `, ${erros.length} falha(s)` : ''}`, {
      module: 'ccts_dashboardscgp',
      action_type: 'busca_mte_massa',
      total: ids.length,
      concluidos,
      erros: erros.length
    }).catch((e) => console.warn('Erro ao registrar log:', e))
  }

  const handleCloseBuscaMteMassaModal = () => {
    if (buscaMteMassaStatus === 'success' || buscaMteMassaStatus === 'error') {
      fetchConvencoes()
      setSelectedForBuscaMteIds(new Set())
      // Invalidar histórico dos grupos expandidos para recarregar a exibição após a busca
      setHistoricoBuscas(prev => {
        const next = new Map(prev)
        expandedGroups.forEach(k => next.delete(k))
        return next
      })
      setHistoricoBuscasLoading(prev => {
        const next = new Set(prev)
        expandedGroups.forEach(k => next.delete(k))
        return next
      })
    }
    setIsBuscaMteMassaModalOpen(false)
    setBuscaMteMassaStatus('idle')
    setBuscaMteMassaProgress({ total: 0, concluidos: 0, erros: [] })
  }

  const handleCancelBuscaMteMassaModal = () => {
    setIsBuscaMteMassaModalOpen(false)
    setBuscaMteMassaStatus('idle')
    setBuscaMteMassaProgress({ total: 0, concluidos: 0, erros: [] })
  }

  const handleEnviarParaScgp = (id: number, idBusca?: number) => {
    if (!canInsert) return
    setEnviarScgpModalConvencaoId(id)
    setEnviarScgpModalIdBusca(idBusca ?? null)
    setEnviarScgpModalStatus('confirm')
    setEnviarScgpModalError(null)
    setIsEnviarScgpModalOpen(true)
  }

  const handleConfirmEnviarScgp = async () => {
    if (!canInsert) return
    const id = enviarScgpModalConvencaoId
    if (id == null) return
    setEnviarScgpModalStatus('loading')
    try {
      const body: { id: number; id_busca?: number } = { id }
      const idBusca = enviarScgpModalIdBusca
      if (idBusca != null) body.id_busca = idBusca
      const res = await fetch(WEBHOOK_INSERIR_CCT_SCGP, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      })
      if (!res.ok) throw new Error(`Erro ${res.status}`)
      setEnviarScgpModalStatus('success')
      logAction('other', 'other', null, `Enviou convenção ID ${id} para SCGP`, {
        convencao_id: id,
        module: 'ccts_dashboardscgp',
        action_type: 'enviar_para_scgp'
      }).catch((e) => console.warn('Erro ao registrar log:', e))
    } catch (err: any) {
      console.error('Erro ao enviar para SCGP:', err)
      setEnviarScgpModalError(err?.message ?? 'Não foi possível enviar para o SCGP. Tente novamente.')
      setEnviarScgpModalStatus('error')
    }
  }

  const handleCloseEnviarScgpModal = () => {
    if (enviarScgpModalStatus === 'success' || enviarScgpModalStatus === 'error') {
      // Recarregar somente o histórico de buscas do registro expandido (não recarrega convenções)
      setHistoricoBuscas(prev => {
        const next = new Map(prev)
        expandedGroups.forEach(k => next.delete(k))
        return next
      })
      setHistoricoBuscasLoading(prev => {
        const next = new Set(prev)
        expandedGroups.forEach(k => next.delete(k))
        return next
      })
    }
    setIsEnviarScgpModalOpen(false)
    setEnviarScgpModalStatus('idle')
    setEnviarScgpModalError(null)
    setEnviarScgpModalConvencaoId(null)
    setEnviarScgpModalIdBusca(null)
  }

  const handleCancelEnviarScgpModal = () => {
    setIsEnviarScgpModalOpen(false)
    setEnviarScgpModalStatus('idle')
    setEnviarScgpModalError(null)
    setEnviarScgpModalConvencaoId(null)
    setEnviarScgpModalIdBusca(null)
  }

  useEffect(() => {
    if (!canInsert && selectedForBuscaMteIds.size > 0) {
      setSelectedForBuscaMteIds(new Set())
    }
  }, [canInsert, selectedForBuscaMteIds])

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '—'
    try {
      return new Date(dateString).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      })
    } catch {
      return dateString
    }
  }

  // Exibe data/hora exatamente como está no banco (já em horário Brasil)
  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return '—'
    try {
      const s = String(dateString).trim()
      // Regex único: YYYY-MM-DD seguido de T ou espaço e HH:mm (ex: 2026-02-11 11:37:59 ou 2026-02-11T11:37:59.893Z)
      const fullMatch = s.match(/^(\d{4})-(\d{2})-(\d{2})[T\s](\d{1,2}):(\d{2})/)
      if (fullMatch) {
        const [, y, m, d, h, min] = fullMatch
        return `${d}/${m}/${y} ${h.padStart(2, '0')}:${min}`
      }
      const dateOnly = s.match(/^(\d{4})-(\d{2})-(\d{2})/)
      if (dateOnly) {
        return `${dateOnly[3]}/${dateOnly[2]}/${dateOnly[1]} 00:00`
      }
      return dateString
    } catch {
      return dateString
    }
  }

  // Debug: verificar se os dados foram carregados
  useEffect(() => {
    console.log('CCTSDashboardScgp - Estado:', {
      loading,
      error,
      convencoesCount: convencoesData.length,
      categoriasCount: categoriasData.length,
      convencoesFiltradasCount: convencoesFiltradas.length
    })
  }, [loading, error, convencoesData.length, categoriasData.length, convencoesFiltradas.length])

  if (permissionLoading) {
    return (
      <div className="flex h-screen bg-gray-100 dark:bg-gray-900">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <LoadingSpinner size="lg" className="mx-auto mb-4" />
            <Text size="lg" className="text-gray-600 dark:text-gray-400">
              Verificando permissões...
            </Text>
          </div>
        </div>
      </div>
    )
  }

  if (!hasPermission) {
    return (
      <div className="flex h-screen bg-gray-100 dark:bg-gray-900">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Text size="xl" className="text-gray-500 dark:text-gray-400 mb-2">
              Acesso Negado
            </Text>
            <Text size="sm" color="muted">
              Você não tem permissão para acessar esta página.
            </Text>
            <Text size="sm" color="muted" className="mt-2">
              Módulo: ccts_dashboardscgp
            </Text>
          </div>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex h-screen bg-gray-100 dark:bg-gray-900">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <LoadingSpinner size="lg" className="mx-auto mb-4" />
            <Text size="lg" className="text-gray-600 dark:text-gray-400">
              Carregando convenções...
            </Text>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex h-screen bg-gray-100 dark:bg-gray-900">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <ExclamationTriangleIcon className="h-8 w-8 text-red-500 mx-auto mb-4" />
            <Text size="lg" className="text-red-600 dark:text-red-400">
              Erro ao carregar convenções: {error}
            </Text>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-screen bg-gray-100 dark:bg-gray-900">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-x-hidden overflow-y-auto">
          <div className="h-full flex flex-col">
            {/* Header */}
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <Heading as="h1" size="3xl" className="text-gray-900 dark:text-white">
                    {moduleTitle}
                  </Heading>
                  <Text size="sm" color="muted" className="mt-2">
                    {moduleDescription}
                  </Text>
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  <Tooltip content="Recarregar convenções do banco sem sair da página">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => fetchConvencoes({ silent: true })}
                      disabled={isRefreshing}
                      className="inline-flex items-center gap-2"
                      aria-label="Atualizar dados das convenções"
                    >
                      <ArrowPathIcon
                        className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`}
                        aria-hidden
                      />
                      Atualizar dados
                    </Button>
                  </Tooltip>
                  {refreshError && (
                    <Text size="xs" className="text-red-600 dark:text-red-400 max-w-[16rem] text-right">
                      {refreshError}
                    </Text>
                  )}
                </div>
              </div>
            </div>

            {/* Filtros (card recolhível) */}
            <div className="px-6 py-4">
              <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                <div className="mb-2 flex items-center justify-between">
                  <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                    Filtros
                  </Heading>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setFiltroStatuses([])
                        setFiltroStatusMte([])
                        setFiltroCNPJLaboral('')
                        setFiltroCNPJPatronal('')
                        setFiltroCategorias([])
                        setFiltroMeioRecebimento([])
                        setFiltroStatusValidacao([])
                        setFiltroDataInicioVigencia('')
                        setFiltroDataFimVigencia('')
                        setFiltroDataUltimaBusca('')
                        setTipoFiltroDataInicio('data')
                        setTipoFiltroDataFim('data')
                        setTipoFiltroDataUltimaBusca('data')
                        setFiltroIdScgp('')
                        setFiltroNumeroMte('')
                        setFiltroConveniosQtMaiorQueZero(false)
                        setFiltroNumeroMtePresenca('qualquer')
                      }}
                    >
                      Limpar Filtros
                    </Button>
                    <Button
                      onClick={() => setFiltrosExpandidos(prev => !prev)}
                      variant="outline"
                      size="sm"
                      className="flex items-center gap-2"
                    >
                      <FunnelIcon className="h-4 w-4" />
                      {filtrosExpandidos ? 'Ocultar Filtros' : 'Mostrar Filtros'}
                    </Button>
                  </div>
                </div>
                <div className={`transition-all duration-300 ease-in-out ${
                  filtrosExpandidos ? 'max-h-[4000px] opacity-100 overflow-visible' : 'max-h-0 opacity-0 overflow-hidden'
                }`}>
                {filtrosExpandidos && (
                <>
                {/* Seção SCGP */}
                <div className="mb-6 space-y-6">
                  <Text size="xs" className="text-gray-500 dark:text-gray-400 uppercase tracking-wide font-bold pb-2 border-b border-gray-200 dark:border-gray-700 block">
                    Informações do SCGP
                  </Text>

                  {/* Identificação (IDs e CNPJs) */}
                  <div>
                    <Text size="xs" className="text-gray-600 dark:text-gray-400 font-semibold mb-3 block">
                      Identificação
                    </Text>
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 items-end">
                    <div className="min-w-0">
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                        ID SCGP
                      </Text>
                      <Input
                        type="text"
                        placeholder="Digite o ID SCGP..."
                        value={filtroIdScgp}
                        onChange={(e) => setFiltroIdScgp(e.target.value)}
                        className="w-full"
                      />
                    </div>
                    <div className="min-w-0">
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                        Nº MTE
                      </Text>
                      <Input
                        type="text"
                        placeholder="Digite o Nº MTE..."
                        value={filtroNumeroMte}
                        onChange={(e) => setFiltroNumeroMte(e.target.value)}
                        className="w-full"
                      />
                    </div>
                    <div className="min-w-0">
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                        CNPJ entidade laboral
                      </Text>
                      <Input
                        type="text"
                        placeholder="Digite o CNPJ laboral..."
                        value={filtroCNPJLaboral}
                        onChange={(e) => setFiltroCNPJLaboral(e.target.value)}
                        className="w-full"
                      />
                    </div>
                    <div className="min-w-0">
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                        CNPJ entidade patronal
                      </Text>
                      <Input
                        type="text"
                        placeholder="Digite o CNPJ patronal..."
                        value={filtroCNPJPatronal}
                        onChange={(e) => setFiltroCNPJPatronal(e.target.value)}
                        className="w-full"
                      />
                    </div>
                    </div>
                  </div>

                  {/* Classificação */}
                  <div>
                    <Text size="xs" className="text-gray-600 dark:text-gray-400 font-semibold mb-3 block">
                      Classificação
                    </Text>
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 items-end">
                    <div className="min-w-0" ref={statusDropdownRef}>
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                        Status
                      </Text>
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => setStatusDropdownOpen((o) => !o)}
                          className="w-full flex items-center justify-between gap-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-left text-sm text-gray-900 dark:text-gray-100 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 min-h-[38px]"
                        >
                          <span className="truncate">
                            {filtroStatuses.length === 0
                              ? 'Todos os status'
                              : filtroStatuses.length === 1
                                ? filtroStatuses[0]
                                : `${filtroStatuses.length} status selecionados`}
                          </span>
                          <ChevronDownIcon className={`h-4 w-4 flex-shrink-0 text-gray-500 transition-transform ${statusDropdownOpen ? 'rotate-180' : ''}`} />
                        </button>
                        {statusDropdownOpen && (
                          <div className="absolute z-50 mt-1 w-full rounded-md border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-lg max-h-56 overflow-auto">
                            {statusDisponiveis.map((status) => {
                              const isChecked = filtroStatuses.some(s => s.trim().toLowerCase() === status.trim().toLowerCase())
                              return (
                                <label
                                  key={status}
                                  className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer text-sm text-gray-900 dark:text-gray-100"
                                >
                                  <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={() => {
                                      setFiltroStatuses(prev =>
                                        isChecked
                                          ? prev.filter(s => s.trim().toLowerCase() !== status.trim().toLowerCase())
                                          : [...prev, status]
                                      )
                                    }}
                                    className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                                  />
                                  <span>{status}</span>
                                </label>
                              )
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="min-w-0" ref={categoriaDropdownRef}>
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                        Categoria
                      </Text>
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => setCategoriaDropdownOpen((o) => !o)}
                          className="w-full flex items-center justify-between gap-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-left text-sm text-gray-900 dark:text-gray-100 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 min-h-[38px]"
                        >
                          <span className="truncate">
                            {filtroCategorias.length === 0
                              ? 'Todas as categorias'
                              : filtroCategorias.length === 1
                                ? (categoriasMap.get(filtroCategorias[0]) || '1 selecionada')
                                : `${filtroCategorias.length} selecionadas`}
                          </span>
                          <ChevronDownIcon className={`h-4 w-4 flex-shrink-0 text-gray-500 transition-transform ${categoriaDropdownOpen ? 'rotate-180' : ''}`} />
                        </button>
                        {categoriaDropdownOpen && (
                          <div className="absolute z-50 mt-1 w-full rounded-md border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-lg max-h-56 overflow-auto">
                            {categoriasData.map((cat) => {
                              if (cat.id == null) return null
                              const id = String(cat.id)
                              const label = cat.nome || '—'
                              const isChecked = filtroCategorias.includes(id)
                              return (
                                <label
                                  key={id}
                                  className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer text-sm text-gray-900 dark:text-gray-100"
                                  title={label}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={() => {
                                      setFiltroCategorias(prev =>
                                        isChecked
                                          ? prev.filter(v => v !== id)
                                          : [...prev, id]
                                      )
                                    }}
                                    className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                                  />
                                  <span className="truncate">{label}</span>
                                </label>
                              )
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="min-w-0" ref={meioRecebimentoDropdownRef}>
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                        Meio de Recebimento
                      </Text>
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => setMeioRecebimentoDropdownOpen((o) => !o)}
                          className="w-full flex items-center justify-between gap-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-left text-sm text-gray-900 dark:text-gray-100 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 min-h-[38px]"
                        >
                          <span className="truncate">
                            {filtroMeioRecebimento.length === 0
                              ? 'Todos os meios de recebimento'
                              : filtroMeioRecebimento.length === 1
                                ? filtroMeioRecebimento[0]
                                : `${filtroMeioRecebimento.length} selecionados`}
                          </span>
                          <ChevronDownIcon className={`h-4 w-4 flex-shrink-0 text-gray-500 transition-transform ${meioRecebimentoDropdownOpen ? 'rotate-180' : ''}`} />
                        </button>
                        {meioRecebimentoDropdownOpen && (
                          <div className="absolute z-50 mt-1 w-full rounded-md border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-lg max-h-56 overflow-auto">
                            {meioRecebimentoDisponiveis.map((valor) => {
                              const isChecked = filtroMeioRecebimento.some(f => f.trim().toLowerCase() === valor.trim().toLowerCase())
                              return (
                                <label
                                  key={valor}
                                  className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer text-sm text-gray-900 dark:text-gray-100"
                                >
                                  <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={() => {
                                      setFiltroMeioRecebimento(prev =>
                                        isChecked
                                          ? prev.filter(f => f.trim().toLowerCase() !== valor.trim().toLowerCase())
                                          : [...prev, valor]
                                      )
                                    }}
                                    className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                                  />
                                  <span>{valor}</span>
                                </label>
                              )
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="min-w-0" ref={statusValidacaoDropdownRef}>
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                        Status de Validação
                      </Text>
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => setStatusValidacaoDropdownOpen((o) => !o)}
                          className="w-full flex items-center justify-between gap-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-left text-sm text-gray-900 dark:text-gray-100 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 min-h-[38px]"
                        >
                          <span className="truncate">
                            {filtroStatusValidacao.length === 0
                              ? 'Todos os status de validação'
                              : filtroStatusValidacao.length === 1
                                ? filtroStatusValidacao[0]
                                : `${filtroStatusValidacao.length} selecionados`}
                          </span>
                          <ChevronDownIcon className={`h-4 w-4 flex-shrink-0 text-gray-500 transition-transform ${statusValidacaoDropdownOpen ? 'rotate-180' : ''}`} />
                        </button>
                        {statusValidacaoDropdownOpen && (
                          <div className="absolute z-50 mt-1 w-full rounded-md border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-lg max-h-56 overflow-auto">
                            {statusValidacaoDisponiveis.map((valor) => {
                              const isChecked = filtroStatusValidacao.some(f => f.trim().toLowerCase() === valor.trim().toLowerCase())
                              return (
                                <label
                                  key={valor}
                                  className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer text-sm text-gray-900 dark:text-gray-100"
                                >
                                  <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={() => {
                                      setFiltroStatusValidacao(prev =>
                                        isChecked
                                          ? prev.filter(f => f.trim().toLowerCase() !== valor.trim().toLowerCase())
                                          : [...prev, valor]
                                      )
                                    }}
                                    className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                                  />
                                  <span>{valor}</span>
                                </label>
                              )
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                    </div>
                  </div>

                  <div>
                    <Text size="xs" className="text-gray-600 dark:text-gray-400 font-semibold mb-3 block">
                      Vigência
                    </Text>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div className="min-w-0">
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                        Início da vigência
                      </Text>
                      <div className="flex flex-wrap gap-2 items-end">
                        <Select
                          value={operadorFiltroInicioVigencia}
                          onChange={(e) => setOperadorFiltroInicioVigencia(e.target.value as 'igual' | 'a_partir_de')}
                          className="w-24 shrink-0"
                        >
                          <option value="igual">Igual</option>
                          <option value="a_partir_de">A partir de</option>
                        </Select>
                        <Select
                          value={tipoFiltroDataInicio}
                          onChange={(e) => {
                            setTipoFiltroDataInicio(e.target.value as 'data' | 'mes' | 'ano')
                            setFiltroDataInicioVigencia('')
                          }}
                          className="w-24 shrink-0"
                        >
                          <option value="data">Data</option>
                          <option value="mes">Mês</option>
                          <option value="ano">Ano</option>
                        </Select>
                        {tipoFiltroDataInicio === 'data' && (
                          <Input
                            type="date"
                            value={filtroDataInicioVigencia}
                            onChange={(e) => setFiltroDataInicioVigencia(e.target.value)}
                            className="shrink-0"
                            style={{ width: '180px' }}
                          />
                        )}
                        {tipoFiltroDataInicio === 'mes' && (
                          <Input
                            type="month"
                            value={filtroDataInicioVigencia}
                            onChange={(e) => setFiltroDataInicioVigencia(e.target.value)}
                            className="shrink-0"
                            style={{ width: '180px' }}
                          />
                        )}
                        {tipoFiltroDataInicio === 'ano' && (
                          <Input
                            type="number"
                            min={1900}
                            max={2100}
                            placeholder="Ano (ex: 2024)"
                            value={filtroDataInicioVigencia || ''}
                            onChange={(e) => setFiltroDataInicioVigencia(e.target.value.replace(/\D/g, '').slice(0, 4))}
                            className="shrink-0"
                            style={{ width: '100px' }}
                          />
                        )}
                      </div>
                    </div>
                    <div className="min-w-0">
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                        Fim da vigência
                      </Text>
                      <div className="flex flex-wrap gap-2 items-end">
                        <Select
                          value={operadorFiltroFimVigencia}
                          onChange={(e) => setOperadorFiltroFimVigencia(e.target.value as 'igual' | 'ate')}
                          className="w-24 shrink-0"
                        >
                          <option value="igual">Igual</option>
                          <option value="ate">Até</option>
                        </Select>
                        <Select
                          value={tipoFiltroDataFim}
                          onChange={(e) => {
                            setTipoFiltroDataFim(e.target.value as 'data' | 'mes' | 'ano')
                            setFiltroDataFimVigencia('')
                          }}
                          className="w-24 shrink-0"
                        >
                          <option value="data">Data</option>
                          <option value="mes">Mês</option>
                          <option value="ano">Ano</option>
                        </Select>
                        {tipoFiltroDataFim === 'data' && (
                          <Input
                            type="date"
                            value={filtroDataFimVigencia}
                            onChange={(e) => setFiltroDataFimVigencia(e.target.value)}
                            className="shrink-0"
                            style={{ width: '180px' }}
                          />
                        )}
                        {tipoFiltroDataFim === 'mes' && (
                          <Input
                            type="month"
                            value={filtroDataFimVigencia}
                            onChange={(e) => setFiltroDataFimVigencia(e.target.value)}
                            className="shrink-0"
                            style={{ width: '180px' }}
                          />
                        )}
                        {tipoFiltroDataFim === 'ano' && (
                          <Input
                            type="number"
                            min={1900}
                            max={2100}
                            placeholder="Ano (ex: 2024)"
                            value={filtroDataFimVigencia || ''}
                            onChange={(e) => setFiltroDataFimVigencia(e.target.value.replace(/\D/g, '').slice(0, 4))}
                            className="shrink-0"
                            style={{ width: '100px' }}
                          />
                        )}
                      </div>
                    </div>
                    </div>
                  </div>

                  <div className="rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-700/60 px-4 py-3">
                    <Text size="xs" className="text-gray-600 dark:text-gray-400 font-semibold mb-2 block">
                      Listagem
                    </Text>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 items-start">
                      <div className="relative group min-w-0">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={filtroConveniosQtMaiorQueZero}
                            onChange={(e) => setFiltroConveniosQtMaiorQueZero(e.target.checked)}
                            className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 shrink-0"
                          />
                          <Text size="sm" className="text-gray-700 dark:text-gray-300">
                            Somente CCTs com subgrupos ativos
                          </Text>
                        </label>
                        <div className="absolute bottom-full left-0 mb-2 px-3 py-2 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-[100] w-64 text-left leading-relaxed">
                          Exclui da base e da relação os registros com Nº subgrupos nulo ou zero. Indicadores e listas consideram apenas Nº subgrupos &gt; 0.
                          <div className="absolute top-full left-4 -mt-1 border-4 border-transparent border-t-gray-900 dark:border-t-gray-700" />
                        </div>
                      </div>
                      <div className="relative group min-w-0">
                        <label htmlFor="filtro-numero-mte-presenca" className="flex items-center gap-3 w-full min-w-0">
                          <Text size="xs" className="text-gray-600 dark:text-gray-400 font-medium shrink-0 whitespace-nowrap">
                            Número de registro no MTE
                          </Text>
                          <div className="flex-1 min-w-0">
                            <Select
                              id="filtro-numero-mte-presenca"
                              size="sm"
                              value={filtroNumeroMtePresenca}
                              onChange={(e) => setFiltroNumeroMtePresenca(e.target.value as FiltroNumeroMtePresenca)}
                              aria-label="Filtrar listagem por presença do número de registro no MTE"
                            >
                              <option value="qualquer">Todos (com ou sem Nº MTE)</option>
                              <option value="sem">Somente sem Nº MTE (vazio)</option>
                              <option value="com">Somente com Nº MTE preenchido</option>
                            </Select>
                          </div>
                        </label>
                        <div className="absolute bottom-full left-0 mb-2 px-3 py-2 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-[100] w-72 text-left leading-relaxed">
                          <span className="font-semibold block mb-1">Todos:</span>
                          não restringe pelo campo Nº MTE.
                          <span className="font-semibold block mt-2 mb-1">Sem Nº MTE:</span>
                          apenas registros com campo vazio ou nulo.
                          <span className="font-semibold block mt-2 mb-1">Com Nº MTE:</span>
                          apenas registros que já possuem número de registro no MTE.
                          <div className="absolute top-full left-4 -mt-1 border-4 border-transparent border-t-gray-900 dark:border-t-gray-700" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Seção MTE */}
                <div className="mb-6 space-y-4">
                  <Text size="xs" className="text-gray-500 dark:text-gray-400 uppercase tracking-wide font-bold pb-2 border-b border-gray-200 dark:border-gray-700 block">
                    Informações do MTE
                  </Text>
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-6 gap-y-5 items-end">
                    <div className="lg:col-span-3 min-w-0" ref={statusMteDropdownRef}>
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                        Busca MTE
                      </Text>
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => setStatusMteDropdownOpen((o) => !o)}
                          className="w-full flex items-center justify-between gap-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-left text-sm text-gray-900 dark:text-gray-100 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 min-h-[38px]"
                        >
                          <span className="truncate">
                            {filtroStatusMte.length === 0
                              ? 'Todos'
                              : filtroStatusMte.length === 1
                                ? filtroStatusMte[0]
                                : `${filtroStatusMte.length} selecionados`}
                          </span>
                          <ChevronDownIcon className={`h-4 w-4 flex-shrink-0 text-gray-500 transition-transform ${statusMteDropdownOpen ? 'rotate-180' : ''}`} />
                        </button>
                        {statusMteDropdownOpen && (
                          <div className="absolute z-50 mt-1 w-full rounded-md border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-lg max-h-56 overflow-auto">
                            {statusMteDisponiveis.map((valor) => {
                              const isChecked = filtroStatusMte.includes(valor)
                              return (
                                <label
                                  key={valor}
                                  className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer text-sm text-gray-900 dark:text-gray-100"
                                >
                                  <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={() => {
                                      setFiltroStatusMte(prev =>
                                        isChecked ? prev.filter(s => s !== valor) : [...prev, valor]
                                      )
                                    }}
                                    className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                                  />
                                  <span>{valor}</span>
                                </label>
                              )
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="lg:col-span-9 min-w-0">
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                        Data última busca MTE
                      </Text>
                      <div className="flex flex-nowrap gap-2 items-end min-w-0">
                        <Select
                          value={operadorFiltroDataUltimaBusca}
                          onChange={(e) => setOperadorFiltroDataUltimaBusca(e.target.value as 'igual' | 'a_partir_de' | 'ate')}
                          className="w-32 min-w-[8rem] shrink-0"
                        >
                          <option value="igual">Igual</option>
                          <option value="a_partir_de">A partir de</option>
                          <option value="ate">Até</option>
                        </Select>
                        <Select
                          value={tipoFiltroDataUltimaBusca}
                          onChange={(e) => {
                            const novo = e.target.value as 'data' | 'mes' | 'ano' | 'hoje'
                            setTipoFiltroDataUltimaBusca(novo)
                            if (novo === 'hoje') {
                              setFiltroDataUltimaBusca(new Date().toISOString().slice(0, 10))
                            } else {
                              setFiltroDataUltimaBusca('')
                            }
                          }}
                          className="w-28 min-w-[7rem] shrink-0"
                        >
                          <option value="data">Data</option>
                          <option value="hoje">Hoje</option>
                          <option value="mes">Mês</option>
                          <option value="ano">Ano</option>
                        </Select>
                        {tipoFiltroDataUltimaBusca === 'data' && (
                          <Input
                            type="date"
                            value={filtroDataUltimaBusca}
                            onChange={(e) => setFiltroDataUltimaBusca(e.target.value)}
                            className="w-20 min-w-0 shrink max-w-full"
                          />
                        )}
                        {tipoFiltroDataUltimaBusca === 'hoje' && (
                          <Input
                            type="date"
                            value={filtroDataUltimaBusca || new Date().toISOString().slice(0, 10)}
                            onChange={(e) => setFiltroDataUltimaBusca(e.target.value)}
                            className="w-20 min-w-0 shrink max-w-full"
                          />
                        )}
                        {tipoFiltroDataUltimaBusca === 'mes' && (
                          <Input
                            type="month"
                            value={filtroDataUltimaBusca}
                            onChange={(e) => setFiltroDataUltimaBusca(e.target.value)}
                            className="w-20 min-w-0 shrink max-w-full"
                          />
                        )}
                        {tipoFiltroDataUltimaBusca === 'ano' && (
                          <Input
                            type="number"
                            min={1900}
                            max={2100}
                            placeholder="Ano (ex: 2024)"
                            value={filtroDataUltimaBusca || ''}
                            onChange={(e) => setFiltroDataUltimaBusca(e.target.value.replace(/\D/g, '').slice(0, 4))}
                            className="w-20 shrink-0"
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                </>
                )}
                </div>
              </div>
            </div>

            {/* Abas */}
            <div className="px-6">
              <div className="border-b border-gray-200 dark:border-gray-700 flex gap-2">
                <button
                  onClick={() => setActiveTab('dashboard')}
                  className={`px-4 py-2 -mb-px border-b-2 ${activeTab === 'dashboard' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
                >
                  Dashboard
                </button>
                <button
                  onClick={() => setActiveTab('relacaoAgrupada')}
                  className={`px-4 py-2 -mb-px border-b-2 ${activeTab === 'relacaoAgrupada' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
                >
                  Relação Detalhada
                </button>
              </div>
            </div>

            {/* Conteúdo por Aba */}
            {activeTab === 'dashboard' && (
              <div className="flex-1 px-6 py-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-4">
                  {/* Card 1: Convenções */}
                  <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-shadow duration-200 overflow-visible">
                    <div className="p-5 pb-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Text size="sm" className="text-gray-600 dark:text-gray-300 font-medium uppercase tracking-wide">Total de CCTs</Text>
                        <div className="relative group">
                          <QuestionMarkCircleIcon className="h-4 w-4 text-gray-400 dark:text-gray-500 cursor-help flex-shrink-0 hover:text-gray-600 dark:hover:text-gray-300 transition-colors" />
                          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-[100] w-64 text-left leading-relaxed">
                            Quantidade de convenções únicas (id_scgp distintos). Considera todos os filtros, incluindo status (CNPJ, categoria, datas, ID SCGP).
                            <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900 dark:border-t-gray-700" />
                          </div>
                        </div>
                      </div>
                      <Text as="p" size="4xl" weight="bold" className="text-gray-900 dark:text-white tracking-tight tabular-nums">{dashboardStats.total}</Text>
                    </div>
                    <div className="px-4 pb-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="rounded-xl bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800/50 p-4 min-h-[88px] flex flex-col justify-between">
                          <div className="flex items-center gap-1.5 mb-2">
                            <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 dark:bg-emerald-400" />
                            <Text size="sm" className="text-emerald-700 dark:text-emerald-300 font-semibold">Vigentes</Text>
                            <div className="relative group">
                              <QuestionMarkCircleIcon className="h-4 w-4 text-emerald-600 dark:text-emerald-400 cursor-help flex-shrink-0 opacity-80" />
                              <div className="absolute bottom-full left-0 mb-2 px-3 py-2 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-[100] w-56 text-left leading-relaxed">
                                CCTs com status VIGENTE (id_scgp distintos), mesma base de filtros com status.
                                <div className="absolute top-full left-4 -mt-1 border-4 border-transparent border-t-gray-900 dark:border-t-gray-700" />
                              </div>
                            </div>
                          </div>
                          <Text as="p" size="2xl" weight="bold" className="text-emerald-700 dark:text-emerald-300 tabular-nums">{dashboardStats.totalConvencoesVigentes}</Text>
                        </div>
                        <div className="rounded-xl bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-800/50 p-4 min-h-[88px] flex flex-col justify-between">
                          <div className="flex items-center gap-1.5 mb-2">
                            <span className="inline-block w-2 h-2 rounded-full bg-rose-500 dark:bg-rose-400" />
                            <Text size="sm" className="text-rose-700 dark:text-rose-300 font-semibold">Expiradas</Text>
                            <div className="relative group">
                              <QuestionMarkCircleIcon className="h-4 w-4 text-rose-600 dark:text-rose-400 cursor-help flex-shrink-0 opacity-80" />
                              <div className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-[100] w-56 text-left leading-relaxed">
                                CCTs com status EXPIRADA (id_scgp distintos), mesma base de filtros com status.
                                <div className="absolute top-full right-4 -mt-1 border-4 border-transparent border-t-gray-900 dark:border-t-gray-700" />
                              </div>
                            </div>
                          </div>
                          <Text as="p" size="2xl" weight="bold" className="text-rose-700 dark:text-rose-300 tabular-nums">{dashboardStats.totalConvencoesExpiradas}</Text>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Card 2: Vidas */}
                  <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-shadow duration-200 overflow-visible">
                    <div className="p-5 pb-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Text size="sm" className="text-gray-600 dark:text-gray-300 font-medium uppercase tracking-wide">Vidas com Subgrupos Ativos</Text>
                        <div className="relative group">
                          <QuestionMarkCircleIcon className="h-4 w-4 text-gray-400 dark:text-gray-500 cursor-help flex-shrink-0 hover:text-gray-600 dark:hover:text-gray-300 transition-colors" />
                          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-[100] w-64 text-left leading-relaxed">
                            Soma do número de vidas, contando uma vez por id_scgp distinto (considera filtro de status).
                            <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900 dark:border-t-gray-700" />
                          </div>
                        </div>
                      </div>
                      <Text as="p" size="4xl" weight="bold" className="text-gray-900 dark:text-white tracking-tight tabular-nums">{dashboardStats.totalVidas.toLocaleString('pt-BR')}</Text>
                    </div>
                    <div className="px-4 pb-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="rounded-xl bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800/50 p-4 min-h-[88px] flex flex-col justify-between">
                          <div className="flex items-center gap-1.5 mb-2">
                            <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 dark:bg-emerald-400" />
                            <Text size="sm" className="text-emerald-700 dark:text-emerald-300 font-semibold">Vigentes</Text>
                            <div className="relative group">
                              <QuestionMarkCircleIcon className="h-4 w-4 text-emerald-600 dark:text-emerald-400 cursor-help flex-shrink-0 opacity-80" />
                              <div className="absolute bottom-full left-0 mb-2 px-3 py-2 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-[100] w-56 text-left leading-relaxed">
                                Soma de vidas das CCTs com status VIGENTE (uma vez por id_scgp distinto).
                                <div className="absolute top-full left-4 -mt-1 border-4 border-transparent border-t-gray-900 dark:border-t-gray-700" />
                              </div>
                            </div>
                          </div>
                          <Text as="p" size="2xl" weight="bold" className="text-emerald-700 dark:text-emerald-300 tabular-nums">{dashboardStats.totalVidasVigentes.toLocaleString('pt-BR')}</Text>
                        </div>
                        <div className="rounded-xl bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-800/50 p-4 min-h-[88px] flex flex-col justify-between">
                          <div className="flex items-center gap-1.5 mb-2">
                            <span className="inline-block w-2 h-2 rounded-full bg-rose-500 dark:bg-rose-400" />
                            <Text size="sm" className="text-rose-700 dark:text-rose-300 font-semibold">Expiradas</Text>
                            <div className="relative group">
                              <QuestionMarkCircleIcon className="h-4 w-4 text-rose-600 dark:text-rose-400 cursor-help flex-shrink-0 opacity-80" />
                              <div className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-[100] w-56 text-left leading-relaxed">
                                Soma de vidas das CCTs com status EXPIRADA (uma vez por id_scgp distinto).
                                <div className="absolute top-full right-4 -mt-1 border-4 border-transparent border-t-gray-900 dark:border-t-gray-700" />
                              </div>
                            </div>
                          </div>
                          <Text as="p" size="2xl" weight="bold" className="text-rose-700 dark:text-rose-300 tabular-nums">{dashboardStats.totalVidasExpiradas.toLocaleString('pt-BR')}</Text>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Card 3: Subgrupos */}
                  <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-shadow duration-200 overflow-visible">
                    <div className="p-5 pb-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Text size="sm" className="text-gray-600 dark:text-gray-300 font-medium uppercase tracking-wide">Subgrupos Ativos</Text>
                        <div className="relative group">
                          <QuestionMarkCircleIcon className="h-4 w-4 text-gray-400 dark:text-gray-500 cursor-help flex-shrink-0 hover:text-gray-600 dark:hover:text-gray-300 transition-colors" />
                          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-[100] w-64 text-left leading-relaxed">
                            Soma da quantidade de subgrupos, contando uma vez por id_scgp distinto (considera filtro de status).
                            <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900 dark:border-t-gray-700" />
                          </div>
                        </div>
                      </div>
                      <Text as="p" size="4xl" weight="bold" className="text-gray-900 dark:text-white tracking-tight tabular-nums">{dashboardStats.totalConvenios.toLocaleString('pt-BR')}</Text>
                    </div>
                    <div className="px-4 pb-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="rounded-xl bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800/50 p-4 min-h-[88px] flex flex-col justify-between">
                          <div className="flex items-center gap-1.5 mb-2">
                            <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 dark:bg-emerald-400" />
                            <Text size="sm" className="text-emerald-700 dark:text-emerald-300 font-semibold">Vigentes</Text>
                            <div className="relative group">
                              <QuestionMarkCircleIcon className="h-4 w-4 text-emerald-600 dark:text-emerald-400 cursor-help flex-shrink-0 opacity-80" />
                              <div className="absolute bottom-full left-0 mb-2 px-3 py-2 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-[100] w-56 text-left leading-relaxed">
                                Soma de subgrupos das CCTs com status VIGENTE (uma vez por id_scgp distinto).
                                <div className="absolute top-full left-4 -mt-1 border-4 border-transparent border-t-gray-900 dark:border-t-gray-700" />
                              </div>
                            </div>
                          </div>
                          <Text as="p" size="2xl" weight="bold" className="text-emerald-700 dark:text-emerald-300 tabular-nums">{dashboardStats.totalConveniosVigentes.toLocaleString('pt-BR')}</Text>
                        </div>
                        <div className="rounded-xl bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-800/50 p-4 min-h-[88px] flex flex-col justify-between">
                          <div className="flex items-center gap-1.5 mb-2">
                            <span className="inline-block w-2 h-2 rounded-full bg-rose-500 dark:bg-rose-400" />
                            <Text size="sm" className="text-rose-700 dark:text-rose-300 font-semibold">Expiradas</Text>
                            <div className="relative group">
                              <QuestionMarkCircleIcon className="h-4 w-4 text-rose-600 dark:text-rose-400 cursor-help flex-shrink-0 opacity-80" />
                              <div className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-[100] w-56 text-left leading-relaxed">
                                Soma de subgrupos das CCTs com status EXPIRADA (uma vez por id_scgp distinto).
                                <div className="absolute top-full right-4 -mt-1 border-4 border-transparent border-t-gray-900 dark:border-t-gray-700" />
                              </div>
                            </div>
                          </div>
                          <Text as="p" size="2xl" weight="bold" className="text-rose-700 dark:text-rose-300 tabular-nums">{dashboardStats.totalConveniosExpirados.toLocaleString('pt-BR')}</Text>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="space-y-4">
                  {dashboardStats.statusData.length > 0 && (
                    <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
                      <div className="px-4 py-4 border-b border-gray-200 dark:border-gray-700">
                        <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">Distribuição por Status</Heading>
                      </div>
                      <div className="h-[21.6rem] p-4">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie dataKey="value" data={dashboardStats.statusData} outerRadius={90} label={pieLabelText}>
                              {dashboardStats.statusData.map((d, idx) => {
                                const colors = ["#3b82f6","#10b981","#f59e0b","#ef4444","#8b5cf6","#06b6d4","#ec4899"]
                                const nameUpper = (d.name || '').toUpperCase()
                                const fillColor = nameUpper === 'EXPIRADA' ? '#ef4444' : nameUpper === 'VIGENTE' ? '#22c55e' : nameUpper === 'BUSCADA' || nameUpper === 'SUBSTITUIDA' ? '#2563eb' : colors[idx % 7]
                                return <Cell key={d.name} fill={fillColor} />
                              })}
                            </Pie>
                            <Legend />
                            <RechartsTooltip content={renderPieTooltip} />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  )}
                  {/* Seção: Indicadores de Início e Fim de Vigência */}
                  <div className="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/30">
                      <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">Indicadores por Vigência</Heading>
                    </div>
                    <div className="p-4">
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Início de Vigência */}
                        <div className="rounded-xl border border-blue-200 dark:border-blue-800/50 bg-blue-50/50 dark:bg-blue-950/20 p-4">
                          <div className="flex items-center gap-2 mb-4">
                            <span className="inline-block w-1 h-6 rounded-full bg-blue-500 dark:bg-blue-400" />
                            <Heading as="h4" size="2xl" className="text-blue-800 dark:text-blue-200">Início de Vigência</Heading>
                          </div>
                          <ul className="space-y-3">
                            <li className="flex items-center justify-between gap-4 py-2 px-3 rounded-lg bg-white dark:bg-gray-800 border border-blue-100 dark:border-blue-900/50">
                              <div className="flex items-center gap-1.5 min-w-0">
                                <Text size="sm" className="text-gray-700 dark:text-gray-300">CCTs que iniciaram vigência no último mês</Text>
                                <div className="relative group flex-shrink-0">
                                  <QuestionMarkCircleIcon className="h-4 w-4 text-blue-600 dark:text-blue-400 cursor-help opacity-80" />
                                  <div className="absolute bottom-full left-0 mb-2 px-3 py-2 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-[100] w-56 text-left leading-relaxed">
                                    Somente CCTs cuja data de início de vigência cai no mês anterior (calendário). id_scgp distintos; sem filtro de status.
                                    <div className="absolute top-full left-4 -mt-1 border-4 border-transparent border-t-gray-900 dark:border-t-gray-700" />
                                  </div>
                                </div>
                              </div>
                              <Text as="span" size="xl" weight="bold" className="text-blue-700 dark:text-blue-300 tabular-nums flex-shrink-0">{dashboardStats.cctsInicioUltimoMesCount}</Text>
                            </li>
                            <li className="flex items-center justify-between gap-4 py-2 px-3 rounded-lg bg-white dark:bg-gray-800 border border-blue-100 dark:border-blue-900/50">
                              <div className="flex items-center gap-1.5 min-w-0">
                                <Text size="sm" className="text-gray-700 dark:text-gray-300">CCTs que iniciaram vigência nos últimos 3 meses</Text>
                                <div className="relative group flex-shrink-0">
                                  <QuestionMarkCircleIcon className="h-4 w-4 text-blue-600 dark:text-blue-400 cursor-help opacity-80" />
                                  <div className="absolute bottom-full left-0 mb-2 px-3 py-2 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-[100] w-56 text-left leading-relaxed">
                                    Somente CCTs cuja data de início de vigência está dentro dos últimos 3 meses (calendário). id_scgp distintos; sem filtro de status.
                                    <div className="absolute top-full left-4 -mt-1 border-4 border-transparent border-t-gray-900 dark:border-t-gray-700" />
                                  </div>
                                </div>
                              </div>
                              <Text as="span" size="xl" weight="bold" className="text-blue-700 dark:text-blue-300 tabular-nums flex-shrink-0">{dashboardStats.cctsInicioUltimos3MesesCount}</Text>
                            </li>
                            <li className="flex items-center justify-between gap-4 py-2 px-3 rounded-lg bg-white dark:bg-gray-800 border border-blue-100 dark:border-blue-900/50">
                              <div className="flex items-center gap-1.5 min-w-0">
                                <Text size="sm" className="text-gray-700 dark:text-gray-300">Nº de Vidas que a CCT iniciou a vigência no último mês</Text>
                                <div className="relative group flex-shrink-0">
                                  <QuestionMarkCircleIcon className="h-4 w-4 text-blue-600 dark:text-blue-400 cursor-help opacity-80" />
                                  <div className="absolute bottom-full left-0 mb-2 px-3 py-2 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-[100] w-64 text-left leading-relaxed">
                                    Soma de num_vidas (uma vez por id_scgp) das CCTs cuja data de início de vigência cai somente no mês anterior (calendário). Sem filtro de status.
                                    <div className="absolute top-full left-4 -mt-1 border-4 border-transparent border-t-gray-900 dark:border-t-gray-700" />
                                  </div>
                                </div>
                              </div>
                              <Text as="span" size="xl" weight="bold" className="text-blue-700 dark:text-blue-300 tabular-nums flex-shrink-0">
                                {dashboardStats.vidasInicioUltimoMes.toLocaleString('pt-BR')}
                              </Text>
                            </li>
                            <li className="flex items-center justify-between gap-4 py-2 px-3 rounded-lg bg-white dark:bg-gray-800 border border-blue-100 dark:border-blue-900/50">
                              <div className="flex items-center gap-1.5 min-w-0">
                                <Text size="sm" className="text-gray-700 dark:text-gray-300">Nº de Vidas que a CCT iniciou a vigência nos últimos 3 meses</Text>
                                <div className="relative group flex-shrink-0">
                                  <QuestionMarkCircleIcon className="h-4 w-4 text-blue-600 dark:text-blue-400 cursor-help opacity-80" />
                                  <div className="absolute bottom-full left-0 mb-2 px-3 py-2 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-[100] w-64 text-left leading-relaxed">
                                    Soma de num_vidas (uma vez por id_scgp) das CCTs cuja data de início de vigência está somente dentro dos últimos 3 meses (calendário). Sem filtro de status.
                                    <div className="absolute top-full left-4 -mt-1 border-4 border-transparent border-t-gray-900 dark:border-t-gray-700" />
                                  </div>
                                </div>
                              </div>
                              <Text as="span" size="xl" weight="bold" className="text-blue-700 dark:text-blue-300 tabular-nums flex-shrink-0">
                                {dashboardStats.vidasInicioUltimos3Meses.toLocaleString('pt-BR')}
                              </Text>
                            </li>
                          </ul>
                        </div>
                        {/* Fim de Vigência */}
                        <div className="rounded-xl border border-red-200 dark:border-red-800/50 bg-red-50/50 dark:bg-red-950/20 p-4">
                          <div className="flex items-center gap-2 mb-4">
                            <span className="inline-block w-1 h-6 rounded-full bg-red-500 dark:bg-red-400" />
                            <Heading as="h4" size="2xl" className="text-red-800 dark:text-red-200">Fim de Vigência</Heading>
                          </div>
                          <ul className="space-y-3">
                            <li className="flex items-center justify-between gap-4 py-2 px-3 rounded-lg bg-white dark:bg-gray-800 border border-red-100 dark:border-red-900/50">
                              <div className="flex items-center gap-1.5 min-w-0">
                                <Text size="sm" className="text-gray-700 dark:text-gray-300">CCTs vigentes até o próximo mês</Text>
                                <div className="relative group flex-shrink-0">
                                  <QuestionMarkCircleIcon className="h-4 w-4 text-red-600 dark:text-red-400 cursor-help opacity-80" />
                                  <div className="absolute bottom-full left-0 mb-2 px-3 py-2 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-[100] w-56 text-left leading-relaxed">
                                    Somente CCTs com status VIGENTE cuja data de fim de vigência cai no próximo mês (calendário). id_scgp distintos.
                                    <div className="absolute top-full left-4 -mt-1 border-4 border-transparent border-t-gray-900 dark:border-t-gray-700" />
                                  </div>
                                </div>
                              </div>
                              <Text as="span" size="xl" weight="bold" className="text-red-700 dark:text-red-300 tabular-nums flex-shrink-0">{dashboardStats.cctsVigentesAteProximoMesCount}</Text>
                            </li>
                            <li className="flex items-center justify-between gap-4 py-2 px-3 rounded-lg bg-white dark:bg-gray-800 border border-red-100 dark:border-red-900/50">
                              <div className="flex items-center gap-1.5 min-w-0">
                                <Text size="sm" className="text-gray-700 dark:text-gray-300">CCTs vigentes até os próximos 3 meses</Text>
                                <div className="relative group flex-shrink-0">
                                  <QuestionMarkCircleIcon className="h-4 w-4 text-red-600 dark:text-red-400 cursor-help opacity-80" />
                                  <div className="absolute bottom-full left-0 mb-2 px-3 py-2 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-[100] w-56 text-left leading-relaxed">
                                    Somente CCTs com status VIGENTE cuja data de fim de vigência está dentro dos próximos 3 meses (calendário). id_scgp distintos.
                                    <div className="absolute top-full left-4 -mt-1 border-4 border-transparent border-t-gray-900 dark:border-t-gray-700" />
                                  </div>
                                </div>
                              </div>
                              <Text as="span" size="xl" weight="bold" className="text-red-700 dark:text-red-300 tabular-nums flex-shrink-0">{dashboardStats.cctsVigentesAteProximos3MesesCount}</Text>
                            </li>
                            <li className="flex items-center justify-between gap-4 py-2 px-3 rounded-lg bg-white dark:bg-gray-800 border border-red-100 dark:border-red-900/50">
                              <div className="flex items-center gap-1.5 min-w-0">
                                <Text size="sm" className="text-gray-700 dark:text-gray-300">Nº de Vidas com CCT vigente até o próximo mês</Text>
                                <div className="relative group flex-shrink-0">
                                  <QuestionMarkCircleIcon className="h-4 w-4 text-red-600 dark:text-red-400 cursor-help opacity-80" />
                                  <div className="absolute bottom-full left-0 mb-2 px-3 py-2 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-[100] w-56 text-left leading-relaxed">
                                    Soma de num_vidas (uma vez por id_scgp) dos CCTs com status VIGENTE cujo fim de vigência cai somente no próximo mês (calendário).
                                    <div className="absolute top-full left-4 -mt-1 border-4 border-transparent border-t-gray-900 dark:border-t-gray-700" />
                                  </div>
                                </div>
                              </div>
                              <Text as="span" size="xl" weight="bold" className="text-red-700 dark:text-red-300 tabular-nums flex-shrink-0">{dashboardStats.vidasVigentesAteProximoMes.toLocaleString('pt-BR')}</Text>
                            </li>
                            <li className="flex items-center justify-between gap-4 py-2 px-3 rounded-lg bg-white dark:bg-gray-800 border border-red-100 dark:border-red-900/50">
                              <div className="flex items-center gap-1.5 min-w-0">
                                <Text size="sm" className="text-gray-700 dark:text-gray-300">Nº de Vidas com CCT vigente até os próximos 3 meses</Text>
                                <div className="relative group flex-shrink-0">
                                  <QuestionMarkCircleIcon className="h-4 w-4 text-red-600 dark:text-red-400 cursor-help opacity-80" />
                                  <div className="absolute bottom-full left-0 mb-2 px-3 py-2 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-[100] w-56 text-left leading-relaxed">
                                    Soma de num_vidas (uma vez por id_scgp) dos CCTs com status VIGENTE cujo fim de vigência está somente dentro dos próximos 3 meses (calendário).
                                    <div className="absolute top-full left-4 -mt-1 border-4 border-transparent border-t-gray-900 dark:border-t-gray-700" />
                                  </div>
                                </div>
                              </div>
                              <Text as="span" size="xl" weight="bold" className="text-red-700 dark:text-red-300 tabular-nums flex-shrink-0">{dashboardStats.vidasVigentesAteProximos3Meses.toLocaleString('pt-BR')}</Text>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                  {dashboardStats.categoriaData.length > 0 && (
                    <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
                      <div className="px-4 py-4 border-b border-gray-200 dark:border-gray-700">
                        <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">Distribuição por Categoria (Top 10)</Heading>
                      </div>
                      <div className="h-[21.6rem] p-4">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={dashboardStats.categoriaData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis 
                              dataKey="name" 
                              angle={-45}
                              textAnchor="end"
                              height={100}
                              interval={0}
                              tick={{ fontSize: 12 }}
                            />
                            <YAxis />
                            <RechartsTooltip 
                              formatter={(value: number) => [value, 'Quantidade']}
                              labelStyle={{ color: '#000' }}
                            />
                            <Bar dataKey="value" fill="#3b82f6" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  )}
                  {dashboardStats.cctPorFimVigenciaData.length > 0 && (
                    <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
                      <div className="px-4 py-4 border-b border-gray-200 dark:border-gray-700">
                        <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">Número de CCTs por Fim de Vigência</Heading>
                      </div>
                      <div className="h-[19rem] px-4 pt-4 pb-0 overflow-hidden">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={dashboardStats.cctPorFimVigenciaData} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis 
                              dataKey="name" 
                              angle={-45}
                              textAnchor="end"
                              height={50}
                              interval={0}
                              tick={{ fontSize: 11 }}
                            />
                            <YAxis />
                            <RechartsTooltip 
                              formatter={(value: number) => [value, 'CCTs']}
                              labelFormatter={(label) => `Fim vigência: ${label}`}
                              labelStyle={{ color: '#000' }}
                            />
                            <Bar dataKey="value" fill="#10b981" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}


            {activeTab === 'relacaoAgrupada' && (
              <div className="flex-1 px-6 py-4">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                  <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2 flex-wrap">
                      {canInsert && idsExpiradasFiltradas.length > 0 && (
                        <button
                          type="button"
                          onClick={() => setSelectedForBuscaMteIds(new Set(idsExpiradasFiltradas))}
                          disabled={isBuscaMteMassaModalOpen}
                          className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline focus:outline-none disabled:opacity-50 disabled:pointer-events-none disabled:no-underline"
                          aria-label="Selecionar todas convenções expiradas do filtro atual para busca no MTE"
                        >
                          Selecionar todos (filtrados){` (${idsExpiradasFiltradas.length})`}
                        </button>
                      )}
                      {canInsert && selectedForBuscaMteIds.size > 0 && (
                        <>
                          <Text size="sm" className="text-gray-600 dark:text-gray-400">
                            {selectedForBuscaMteIds.size} selecionado(s)
                          </Text>
                          <button
                            type="button"
                            onClick={() => setSelectedForBuscaMteIds(new Set())}
                            className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline focus:outline-none"
                          >
                            Limpar seleção
                          </button>
                          <Button
                            size="sm"
                            variant="solid"
                            onClick={() => {
                              setBuscaMteMassaStatus('confirm')
                              setIsBuscaMteMassaModalOpen(true)
                            }}
                            disabled={isBuscaMteMassaModalOpen}
                          >
                            Buscar no MTE em massa
                          </Button>
                        </>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => scrollTabelaBy(-320)}
                        className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 h-8 w-8 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                        aria-label="Rolar para a esquerda"
                      >
                        <ChevronLeftIcon className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => scrollTabelaBy(320)}
                        className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 h-8 w-8 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                        aria-label="Rolar para a direita"
                      >
                        <ChevronRightIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  <div className="flex-1 overflow-auto">
                    <div ref={tabelaScrollRef} className="overflow-x-auto" onWheel={handleTabelaWheel}>
                      <table className="w-full text-[11px] divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
                          <tr>
                            <th className="px-[0.2rem] py-2 text-center w-12" />
                            <th colSpan={7} className="px-[0.2rem] py-2 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider border-r border-gray-200 dark:border-gray-600">
                              INFORMAÇÕES DA CCT NO SCGP
                            </th>
                            <th colSpan={5} className="px-[0.2rem] py-2 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider border-l border-r border-gray-200 dark:border-gray-600">
                              BUSCA MTE
                            </th>
                          </tr>
                          <tr>
                            <th className="px-[0.2rem] py-3 text-center w-12">
                              {/* Coluna para expandir */}
                            </th>
                            <SortableTableHeader
                              sortKey="id_scgp"
                              currentSort={sortBy}
                              sortOrder={sortOrder}
                              onSort={toggleSort}
                              className="px-[0.2rem] py-3 text-center"
                            >
                              ID SCGP
                            </SortableTableHeader>
                            <SortableTableHeader
                              sortKey="num_processo_mte"
                              currentSort={sortBy}
                              sortOrder={sortOrder}
                              onSort={toggleSort}
                              className="px-[0.2rem] py-3 text-center text-[10px] font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap"
                            >
                              Nº MTE
                            </SortableTableHeader>
                            <SortableTableHeader
                              sortKey="data_inicio_vigencia"
                              currentSort={sortBy}
                              sortOrder={sortOrder}
                              onSort={toggleSort}
                              className="px-[0.2rem] py-3 text-center"
                            >
                              Início Vigência
                            </SortableTableHeader>
                            <SortableTableHeader
                              sortKey="data_fim_vigencia"
                              currentSort={sortBy}
                              sortOrder={sortOrder}
                              onSort={toggleSort}
                              className="px-[0.2rem] py-3 text-center"
                            >
                              Fim Vigência
                            </SortableTableHeader>
                            <SortableTableHeader
                              sortKey="status"
                              currentSort={sortBy}
                              sortOrder={sortOrder}
                              onSort={toggleSort}
                              className="px-[0.2rem] py-3 text-center"
                            >
                              SITUAÇÃO
                            </SortableTableHeader>
                            <SortableTableHeader
                              sortKey="num_vidas"
                              currentSort={sortBy}
                              sortOrder={sortOrder}
                              onSort={toggleSort}
                              className="px-[0.2rem] py-3 text-center"
                            >
                              VIDAS
                            </SortableTableHeader>
                            <SortableTableHeader
                              sortKey="convenios_qt"
                              currentSort={sortBy}
                              sortOrder={sortOrder}
                              onSort={toggleSort}
                              className="px-[0.2rem] py-3 text-center border-r border-gray-200 dark:border-gray-600"
                            >
                              SUBGRUPOS
                            </SortableTableHeader>
                            <SortableTableHeader
                              sortKey="status_mte"
                              currentSort={sortBy}
                              sortOrder={sortOrder}
                              onSort={toggleSort}
                              className="px-[0.2rem] py-3 text-center text-[10px] font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap border-l border-gray-200 dark:border-gray-600"
                            >
                              <span className="flex items-center gap-1.5">
                                <span>STATUS</span>
                                <Tooltip
                                  content={
                                    <div
                                      className="space-y-2 text-left"
                                      style={{
                                        minWidth: 0,
                                        overflowWrap: 'break-word',
                                        wordBreak: 'break-word',
                                        whiteSpace: 'normal'
                                      }}
                                    >
                                      <div>
                                        <span className="font-semibold text-red-400">ERRO:</span>
                                        <span className="block mt-0.5">Ocorreu um erro durante a busca no MTE.</span>
                                      </div>
                                      <div>
                                        <span className="font-semibold text-yellow-400">NÃO LOCALIZADA:</span>
                                        <span className="block mt-0.5">
                                          Busca realizada no Sistema do MTE e não foi encontrada a CCT.
                                        </span>
                                      </div>
                                      <div>
                                        <span className="font-semibold text-green-400">LOCALIZADA:</span>
                                        <span className="block mt-0.5">CCT localizada no sistema do MTE.</span>
                                      </div>
                                    </div>
                                  }
                                  constrainWidth
                                >
                                  <QuestionMarkCircleIcon className="h-4 w-4 text-gray-400 dark:text-gray-500 cursor-help flex-shrink-0 hover:text-gray-600 dark:hover:text-gray-300 transition-colors" />
                                </Tooltip>
                              </span>
                            </SortableTableHeader>
                            <th className="px-[0.2rem] py-3 text-center text-[10px] font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap">
                              <div className="flex items-center justify-center gap-1.5">
                                <span className="leading-tight">
                                  ÚLTIMO STATUS
                                  <br />
                                  (HISTÓRICO)
                                </span>
                                <Tooltip
                                  content={
                                    <div
                                      className="space-y-2 text-left"
                                      style={{
                                        minWidth: 0,
                                        overflowWrap: 'break-word',
                                        wordBreak: 'break-word',
                                        whiteSpace: 'normal'
                                      }}
                                    >
                                      <div>
                                        <span className="font-semibold text-yellow-400">NÃO LOCALIZADA:</span>
                                        <span className="block mt-0.5">
                                          Busca realizada no Sistema do MTE e não foi encontrada a CCT.
                                        </span>
                                      </div>
                                      <div>
                                        <span className="font-semibold text-green-400">IMPORTADO SCGP:</span>
                                        <span className="block mt-0.5">
                                          CCT localizada no sistema do MTE e importada para o SCGP.
                                        </span>
                                      </div>
                                      <div>
                                        <span className="font-semibold text-blue-400">DISPONÍVEL PARA IMPORTAR:</span>
                                        <span className="block mt-0.5">
                                          CCT localizada no sistema do MTE e disponível para ser importada para o SCGP, basta acessar o botão Enviar para o SCGP.
                                        </span>
                                      </div>
                                    </div>
                                  }
                                  constrainWidth
                                >
                                  <QuestionMarkCircleIcon className="h-4 w-4 text-gray-400 dark:text-gray-500 cursor-help flex-shrink-0 hover:text-gray-600 dark:hover:text-gray-300 transition-colors" />
                                </Tooltip>
                              </div>
                            </th>
                            <SortableTableHeader
                              sortKey="data_ultima_busca"
                              currentSort={sortBy}
                              sortOrder={sortOrder}
                              onSort={toggleSort}
                              className="px-[0.2rem] py-3 text-center text-[10px] font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap"
                            >
                              DATA ÚLTIMA
                            </SortableTableHeader>
                            {canInsert && (
                              <>
                                <th className="px-[0.2rem] py-3 text-center text-[10px] font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-24 whitespace-nowrap">
                                  Ações
                                </th>
                                <th className="px-[0.2rem] py-3 text-center w-10">
                                  <input
                                    type="checkbox"
                                    checked={convencoesAgrupadasPaginadas.some((item) => (item.status || '').toUpperCase() === 'EXPIRADA') && convencoesAgrupadasPaginadas.filter((item) => (item.status || '').toUpperCase() === 'EXPIRADA').every((item) => selectedForBuscaMteIds.has(item.id))}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setSelectedForBuscaMteIds((prev) => {
                                          const next = new Set(prev)
                                          convencoesAgrupadasPaginadas.forEach((item) => {
                                            if ((item.status || '').toUpperCase() === 'EXPIRADA') next.add(item.id)
                                          })
                                          return next
                                        })
                                      } else {
                                        setSelectedForBuscaMteIds((prev) => {
                                          const next = new Set(prev)
                                          convencoesAgrupadasPaginadas.forEach((item) => {
                                            if ((item.status || '').toUpperCase() === 'EXPIRADA') next.delete(item.id)
                                          })
                                          return next
                                        })
                                      }
                                    }}
                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700"
                                    aria-label="Selecionar todos na página"
                                  />
                                </th>
                              </>
                            )}
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                          {convencoesAgrupadasPaginadas.map((item, index) => {
                            const groupKey = getGroupKey(item)
                            const isExpanded = expandedGroups.has(groupKey)
                            const registros = (item as any).registros || [item]
                            
                            return (
                              <React.Fragment key={`group-${groupKey}-${index}`}>
                                <tr
                                  data-group-key={groupKey}
                                  className={`cursor-pointer ${(item.status || '').toUpperCase() === 'EXPIRADA' ? 'bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30' : (item.status || '').toUpperCase() === 'VIGENTE' ? 'bg-green-50 dark:bg-green-900/20 hover:bg-green-100 dark:hover:bg-green-900/30' : (item.status || '').toUpperCase() === 'SUBSTITUIDA' ? 'bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30' : 'hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                                  onClick={() => {
                                    setExpandedGroups((prev) => {
                                      if (prev.has(groupKey)) {
                                        const next = new Set(prev)
                                        next.delete(groupKey)
                                        return next
                                      }
                                      return new Set([groupKey])
                                    })
                                  }}
                                >
                                  <td className="px-[0.3rem] py-4 text-center">
                                    <div className="flex items-center justify-center gap-2">
                                      <button
                                        onClick={(e) => toggleGroupExpand(groupKey, e)}
                                        className="inline-flex items-center justify-center rounded-md text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-gray-100 focus:outline-none w-6 h-6 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                        title={isExpanded ? "Recolher" : "Expandir para ver CNPJs"}
                                      >
                                        {isExpanded ? (
                                          <ChevronDownIcon className="h-5 w-5" />
                                        ) : (
                                          <ChevronRightIcon className="h-5 w-5" />
                                        )}
                                      </button>
                                    </div>
                                  </td>
                              <td className="px-[0.3rem] py-4 text-center">
                                <Text size="sm" className="text-gray-700 dark:text-gray-300">
                                  {item.id_scgp ?? '—'}
                                </Text>
                              </td>
                              <td className="px-[0.3rem] py-4 text-center">
                                <Text size="sm" className="text-gray-700 dark:text-gray-300">
                                  {item.num_processo_mte ?? '—'}
                                </Text>
                              </td>
                              <td className="px-[0.3rem] py-4 text-center">
                                <Text size="sm" className="text-gray-500 dark:text-gray-400">
                                  {formatDate(item.data_inicio_vigencia)}
                                </Text>
                              </td>
                              <td className="px-[0.3rem] py-4 text-center">
                                <Text size="sm" className="text-gray-500 dark:text-gray-400">
                                  {formatDate(item.data_fim_vigencia)}
                                </Text>
                              </td>
                              <td className="px-[0.3rem] py-4 text-center">
                                <span className={`text-sm ${(item.status || '').toUpperCase() === 'EXPIRADA' ? 'text-red-600 dark:text-red-400 font-semibold' : (item.status || '').toUpperCase() === 'VIGENTE' ? 'text-green-600 dark:text-green-400 font-semibold' : (item.status || '').toUpperCase() === 'BUSCADA' || (item.status || '').toUpperCase() === 'SUBSTITUIDA' ? 'text-blue-600 dark:text-blue-400 font-semibold' : 'text-gray-700 dark:text-gray-300'}`}>
                                  {item.status || '—'}
                                </span>
                              </td>
                              <td className="px-[0.3rem] py-4 text-center">
                                <Text size="sm" className="text-gray-700 dark:text-gray-300">
                                  {item.num_vidas != null ? item.num_vidas : '—'}
                                </Text>
                              </td>
                              <td className="px-[0.3rem] py-4 text-center border-r border-gray-200 dark:border-gray-600">
                                <Text size="sm" className="text-gray-700 dark:text-gray-300">
                                  {item.convenios_qt != null ? item.convenios_qt : '—'}
                                </Text>
                              </td>
                              <td className="px-[0.3rem] py-4 text-center border-l border-gray-200 dark:border-gray-600">
                                <span
                                  className={`text-sm ${
                                    (item.status_mte || '').trim().toUpperCase() === 'ERRO'
                                      ? 'text-red-600 dark:text-red-400 font-semibold'
                                      : (item.status_mte || '').trim().toUpperCase() === 'NÃO LOCALIZADA' ||
                                          (item.status_mte || '').trim().toUpperCase() === 'NAO LOCALIZADA'
                                        ? 'text-yellow-600 dark:text-yellow-400 font-semibold'
                                        : (item.status_mte || '').trim().toUpperCase() === 'LOCALIZADA'
                                          ? 'text-green-600 dark:text-green-400 font-semibold'
                                          : 'text-gray-700 dark:text-gray-300'
                                  }`}
                                >
                                  {item.status_mte ?? '—'}
                                </span>
                              </td>
                              <td className="px-[0.3rem] py-4 text-center">
                                {(() => {
                                  const registrosGrupo: ConvencaoData[] = (item as any).registros || [item]
                                  let melhorStatus: { status: string | null; created_at: string } | null = null

                                  registrosGrupo.forEach((registro) => {
                                    const info = ultimaBuscaPorConvencao.get(registro.id)
                                    if (!info) return
                                    if (!melhorStatus) {
                                      melhorStatus = info
                                      return
                                    }
                                    const atualTime = new Date(info.created_at).getTime()
                                    const melhorTime = new Date(melhorStatus.created_at).getTime()
                                    if (
                                      Number.isFinite(atualTime) &&
                                      (!Number.isFinite(melhorTime) || atualTime > melhorTime)
                                    ) {
                                      melhorStatus = info
                                    }
                                  })

                                  if (!melhorStatus) {
                                    return (
                                      <span className="text-sm text-gray-400 dark:text-gray-500">
                                        —
                                      </span>
                                    )
                                  }

                                  const ultimo = melhorStatus as { status: string | null; created_at: string }
                                  const bruto = (ultimo.status || '').trim().toUpperCase()
                                  let label = ultimo.status || '—'
                                  let className = 'text-gray-700 dark:text-gray-300'

                                  if (
                                    bruto === 'NÃO LOCALIZADA MTE' ||
                                    bruto === 'NAO LOCALIZADA MTE' ||
                                    bruto === 'NÃO LOCALIZADA' ||
                                    bruto === 'NAO LOCALIZADA'
                                  ) {
                                    label = 'NÃO LOCALIZADA'
                                    className = 'text-yellow-600 dark:text-yellow-400'
                                  } else if (bruto === 'DISPONÍVEL PARA IMPORTAR') {
                                    className = 'text-blue-600 dark:text-blue-400'
                                  } else if (bruto === 'IMPORTADO SCGP' || bruto === 'IMPORTADO') {
                                    className = 'text-green-600 dark:text-green-400'
                                  }

                                  const partes = label.split(' ').filter(Boolean)
                                  return (
                                    <span className={`text-sm font-medium ${className}`}>
                                      {partes.map((parte, idx) => (
                                        <React.Fragment key={idx}>
                                          {idx > 0 && <br />}
                                          {parte}
                                        </React.Fragment>
                                      ))}
                                    </span>
                                  )
                                })()}
                              </td>
                              <td className="px-[0.3rem] py-4 text-center">
                                <Text size="sm" className="text-gray-700 dark:text-gray-300">
                                  {formatDateTime(item.data_ultima_busca)}
                                </Text>
                              </td>
                              {canInsert && (
                                <>
                                  <td className="px-[0.3rem] py-4 text-center text-sm text-gray-700 dark:text-gray-300" onClick={(e) => e.stopPropagation()}>
                                    <div className="flex items-center justify-center gap-1 flex-wrap">
                                      {(item.status || '').toUpperCase() === 'EXPIRADA' && (
                                        <Tooltip content="Buscar CCT no MTE">
                                          <button
                                            type="button"
                                            disabled={isBuscaMteModalOpen}
                                            onClick={() => handleBuscarCctMte(item.id, item)}
                                            className="inline-flex items-center justify-center rounded-md p-2 text-gray-600 hover:text-blue-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-blue-400 dark:hover:bg-gray-700 focus:outline-none transition-colors disabled:opacity-50 disabled:pointer-events-none"
                                            aria-label="Buscar CCT no MTE"
                                          >
                                            <MagnifyingGlassIcon className="h-5 w-5" />
                                          </button>
                                        </Tooltip>
                                      )}
                                      {(item.status || '').toUpperCase() !== 'EXPIRADA' && (
                                        <Text size="sm" className="text-gray-500 dark:text-gray-400">—</Text>
                                      )}
                                    </div>
                                  </td>
                                  <td className="px-[0.3rem] py-4 text-center" onClick={(e) => e.stopPropagation()}>
                                    {(item.status || '').toUpperCase() === 'EXPIRADA' ? (
                                      <input
                                        type="checkbox"
                                        checked={selectedForBuscaMteIds.has(item.id)}
                                        onChange={(e) => {
                                          e.stopPropagation()
                                          setSelectedForBuscaMteIds((prev) => {
                                            const next = new Set(prev)
                                            if (next.has(item.id)) next.delete(item.id)
                                            else next.add(item.id)
                                            return next
                                          })
                                        }}
                                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700"
                                        aria-label="Selecionar para busca no MTE"
                                      />
                                    ) : (
                                      <span className="inline-block w-4" />
                                    )}
                                  </td>
                                </>
                              )}
                            </tr>
                            {/* Linha expandida com sub-tabela de registros individuais */}
                            {isExpanded && (
                              <tr>
                                <td colSpan={canInsert ? 13 : 11} className={`px-4 py-6 bg-gray-50 dark:bg-gray-900 border-l border-r ${
                                  (item.status || '').toUpperCase() === 'EXPIRADA' 
                                    ? 'border-red-200 dark:border-red-800' 
                                    : (item.status || '').toUpperCase() === 'VIGENTE' 
                                      ? 'border-green-200 dark:border-green-800' 
                                      : (item.status || '').toUpperCase() === 'SUBSTITUIDA' 
                                        ? 'border-blue-200 dark:border-blue-800' 
                                        : 'border-gray-200 dark:border-gray-700'
                                }`}>
                                  <div className="space-y-6">
                                    {/* Informações Principais */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                      <div className="space-y-1">
                                        <Text size="xs" className="text-gray-500 dark:text-gray-400 uppercase tracking-wide font-semibold">
                                          Categoria
                                        </Text>
                                        <Text size="sm" className="text-gray-900 dark:text-gray-100">
                                          {getNomeCategoria(item.cod_categoria) || '—'}
                                        </Text>
                                      </div>
                                      <div className="space-y-1">
                                        <Text size="xs" className="text-gray-500 dark:text-gray-400 uppercase tracking-wide font-semibold">
                                          Meio de Recebimento
                                        </Text>
                                        <div className="flex items-center justify-between">
                                          <Text size="sm" className="text-gray-900 dark:text-gray-100">
                                            {item.meio_recebimento || '—'}
                                          </Text>
                                          <button
                                            type="button"
                                            onClick={() => handleRowClick(item)}
                                            className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 text-sm font-medium shrink-0 flex items-center gap-1 px-2 py-1 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                                          >
                                            <MagnifyingGlassIcon className="h-4 w-4" />
                                            Mais informações
                                          </button>
                                        </div>
                                      </div>
                                      <div className="space-y-1">
                                        <Text size="xs" className="text-gray-500 dark:text-gray-400 uppercase tracking-wide font-semibold">
                                          Status de Validação
                                        </Text>
                                        <div>
                                          {item.status_validacao ? (
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                                              {item.status_validacao}
                                            </span>
                                          ) : (
                                            <Text size="sm" className="text-gray-500 dark:text-gray-400">—</Text>
                                          )}
                                        </div>
                                      </div>
                                      <div className="space-y-1">
                                        <Text size="xs" className="text-gray-500 dark:text-gray-400 uppercase tracking-wide font-semibold">
                                          Observação de Validação
                                        </Text>
                                        <Text size="sm" className="text-gray-900 dark:text-gray-100 whitespace-pre-wrap">
                                          {item.observacao_validacao || '—'}
                                        </Text>
                                      </div>
                                    </div>

                                    {/* CNPJs */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                      {/* Lista de CNPJs Patronais */}
                                      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
                                        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                                          <div className="flex items-center justify-between">
                                            <Text size="xs" className="text-gray-500 dark:text-gray-400 uppercase tracking-wide font-semibold">
                                              CNPJs Patronais
                                            </Text>
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-gray-200 text-gray-800 dark:bg-gray-600 dark:text-gray-200">
                                              {(() => {
                                                const cnpjPatronaisUnicos = Array.from(
                                                  new Set(
                                                    registros
                                                      .map((r: ConvencaoData) => r.cnpj_entidade_patronal)
                                                      .filter((cnpj: string | null): cnpj is string => !!cnpj)
                                                  )
                                                )
                                                return cnpjPatronaisUnicos.length
                                              })()}
                                            </span>
                                          </div>
                                        </div>
                                        <div className="max-h-64 overflow-y-auto">
                                          {(() => {
                                            const cnpjPatronaisUnicos: string[] = Array.from(
                                              new Set(
                                                registros
                                                  .map((r: ConvencaoData) => r.cnpj_entidade_patronal)
                                                  .filter((cnpj: string | null): cnpj is string => !!cnpj)
                                              )
                                            )
                                            
                                            if (cnpjPatronaisUnicos.length === 0) {
                                              return (
                                                <div className="px-4 py-8 text-sm text-gray-500 dark:text-gray-400 text-center">
                                                  <div className="text-gray-400 dark:text-gray-500 mb-1">Nenhum CNPJ Patronal encontrado</div>
                                                </div>
                                              )
                                            }
                                            
                                            return cnpjPatronaisUnicos.map((cnpj, index) => (
                                              <div
                                                key={`patronal-${groupKey}-${index}`}
                                                className="px-4 py-2 border-b border-gray-200 dark:border-gray-700 last:border-b-0 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2"
                                              >
                                                <span className="text-xs text-gray-500 dark:text-gray-400 font-medium min-w-[20px]">
                                                  {index + 1}.
                                                </span>
                                                <Text size="sm" className="text-gray-700 dark:text-gray-300 font-mono">
                                                  {cnpj}
                                                </Text>
                                              </div>
                                            ))
                                          })()}
                                        </div>
                                      </div>
                                      
                                      {/* Lista de CNPJs Laborais */}
                                      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
                                        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                                          <div className="flex items-center justify-between">
                                            <Text size="xs" className="text-gray-500 dark:text-gray-400 uppercase tracking-wide font-semibold">
                                              CNPJs Laborais
                                            </Text>
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-gray-200 text-gray-800 dark:bg-gray-600 dark:text-gray-200">
                                              {(() => {
                                                const cnpjLaboraisUnicos = Array.from(
                                                  new Set(
                                                    registros
                                                      .map((r: ConvencaoData) => r.cnpj_entidade_laboral)
                                                      .filter((cnpj: string | null): cnpj is string => !!cnpj)
                                                  )
                                                )
                                                return cnpjLaboraisUnicos.length
                                              })()}
                                            </span>
                                          </div>
                                        </div>
                                        <div className="max-h-64 overflow-y-auto">
                                          {(() => {
                                            const cnpjLaboraisUnicos: string[] = Array.from(
                                              new Set(
                                                registros
                                                  .map((r: ConvencaoData) => r.cnpj_entidade_laboral)
                                                  .filter((cnpj: string | null): cnpj is string => !!cnpj)
                                              )
                                            )
                                            
                                            if (cnpjLaboraisUnicos.length === 0) {
                                              return (
                                                <div className="px-4 py-8 text-sm text-gray-500 dark:text-gray-400 text-center">
                                                  <div className="text-gray-400 dark:text-gray-500 mb-1">Nenhum CNPJ Laboral encontrado</div>
                                                </div>
                                              )
                                            }
                                            
                                            return cnpjLaboraisUnicos.map((cnpj, index) => (
                                              <div
                                                key={`laboral-${groupKey}-${index}`}
                                                className="px-4 py-2 border-b border-gray-200 dark:border-gray-700 last:border-b-0 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2"
                                              >
                                                <span className="text-xs text-gray-500 dark:text-gray-400 font-medium min-w-[20px]">
                                                  {index + 1}.
                                                </span>
                                                <Text size="sm" className="text-gray-700 dark:text-gray-300 font-mono">
                                                  {cnpj}
                                                </Text>
                                              </div>
                                            ))
                                          })()}
                                        </div>
                                      </div>
                                    </div>
                                    {/* Histórico de buscas */}
                                  <div className="mt-6">
                                    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
                                      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                                        <Text size="xs" className="text-gray-500 dark:text-gray-400 uppercase tracking-wide font-semibold">
                                          Histórico de buscas no MTE
                                        </Text>
                                      </div>
                                      {historicoBuscasLoading.has(groupKey) ? (
                                        <div className="flex items-center justify-center gap-2 py-8 text-gray-500 dark:text-gray-400">
                                          <LoadingSpinner size="sm" />
                                          <span className="text-sm">Carregando histórico...</span>
                                        </div>
                                      ) : (() => {
                                        const buscas = historicoBuscas.get(groupKey) || []
                                        if (buscas.length === 0) {
                                          return (
                                            <div className="px-4 py-8 text-sm text-gray-500 dark:text-gray-400 text-center">
                                              <div className="text-gray-400 dark:text-gray-500">Nenhuma busca registrada para esta convenção.</div>
                                            </div>
                                          )
                                        }
                                        return (
                                          <div className="overflow-hidden">
                                            <div className="max-h-72 overflow-y-auto">
                                            <table className="w-full text-[11px] text-center">
                                              <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 text-xs text-gray-500 dark:text-gray-400 uppercase">
                                                <tr>
                                                  <th className="px-[0.3rem] py-2 text-center w-12">#</th>
                                                  <th className="px-[0.3rem] py-2 text-center">Data</th>
                                                  <th className="px-[0.3rem] py-2 text-center">
                                                    <div className="flex items-center justify-center gap-1.5">
                                                      <span>STATUS</span>
                                                      <Tooltip content={
                                                        <div className="space-y-2 text-left" style={{ minWidth: 0, overflowWrap: 'break-word', wordBreak: 'break-word', whiteSpace: 'normal' }}>
                                                          <div>
                                                            <span className="font-semibold text-yellow-400">NÃO LOCALIZADA:</span>
                                                            <span className="block mt-0.5">Busca realizada no Sistema do MTE e não foi encontrada a CCT.</span>
                                                          </div>
                                                          <div>
                                                            <span className="font-semibold text-green-400">IMPORTADO SCGP:</span>
                                                            <span className="block mt-0.5">CCT localizada no sistema do MTE e importada para o SCGP.</span>
                                                          </div>
                                                          <div>
                                                            <span className="font-semibold text-blue-400">DISPONÍVEL PARA IMPORTAR:</span>
                                                            <span className="block mt-0.5">CCT localizada no sistema do MTE e disponível para ser importada para o SCGP, basta acessar o botão Enviar para o SCGP.</span>
                                                          </div>
                                                        </div>
                                                      } constrainWidth>
                                                        <QuestionMarkCircleIcon className="h-4 w-4 text-gray-400 dark:text-gray-500 cursor-help flex-shrink-0 hover:text-gray-600 dark:hover:text-gray-300 transition-colors" />
                                                      </Tooltip>
                                                    </div>
                                                  </th>
                                                  <th className="px-[0.3rem] py-2 text-center">ID SCGP</th>
                                                  <th className="px-[0.3rem] py-2 text-center">Vigência início</th>
                                                  <th className="px-[0.3rem] py-2 text-center">Vigência fim</th>
                                                  <th className="px-[0.3rem] py-2 text-center">N° MTE</th>
                                                  <th className="px-[0.3rem] py-2 text-center">Documento</th>
                                                  <th className="px-[0.3rem] py-2 text-center">Download</th>
                                                  {canInsert && <th className="px-[0.3rem] py-2 text-center w-20">Ações</th>}
                                                </tr>
                                              </thead>
                                              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                                {buscas.map((busca, index) => (
                                                  <tr
                                                    key={busca.id}
                                                    className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                                                    onClick={() => {
                                                      setSelectedBuscaHistorico(busca)
                                                      setIsHistoricoBuscaModalOpen(true)
                                                    }}
                                                  >
                                                    <td className="px-[0.3rem] py-2 text-center text-gray-500 dark:text-gray-400 text-xs font-medium">{index + 1}</td>
                                                    <td className="px-[0.3rem] py-2 text-center text-gray-700 dark:text-gray-300 whitespace-nowrap">{formatDate(busca.created_at)}</td>
                                                    <td className="px-[0.3rem] py-2 text-center">
                                                      {(() => {
                                                        const sRaw = (busca.status || '').trim().toUpperCase()
                                                        let label = busca.status ?? '—'
                                                        let className = 'text-gray-700 dark:text-gray-300'

                                                        if (sRaw === 'NÃO LOCALIZADA MTE' || sRaw === 'NAO LOCALIZADA MTE' || sRaw === 'NÃO LOCALIZADA' || sRaw === 'NAO LOCALIZADA') {
                                                          label = 'NÃO LOCALIZADA'
                                                          className = 'text-yellow-600 dark:text-yellow-400'
                                                        } else if (sRaw === 'DISPONÍVEL PARA IMPORTAR') {
                                                          className = 'text-blue-600 dark:text-blue-400'
                                                        } else if (sRaw === 'IMPORTADO SCGP' || sRaw === 'IMPORTADO') {
                                                          className = 'text-green-600 dark:text-green-400'
                                                        }

                                                        const partes = label.split(' ').filter(Boolean)

                                                        return (
                                                          <span className={`text-sm font-medium ${className}`}>
                                                            {partes.map((parte, idx) => (
                                                              <React.Fragment key={idx}>
                                                                {idx > 0 && <br />}
                                                                {parte}
                                                              </React.Fragment>
                                                            ))}
                                                          </span>
                                                        )
                                                      })()}
                                                    </td>
                                                    <td className="px-[0.3rem] py-2 text-center text-gray-700 dark:text-gray-300 whitespace-nowrap">{busca.id_scgp ?? '—'}</td>
                                                    <td className="px-[0.3rem] py-2 text-center text-gray-700 dark:text-gray-300 whitespace-nowrap">{formatDate(busca.vigencia_inicio)}</td>
                                                    <td className="px-[0.3rem] py-2 text-center text-gray-700 dark:text-gray-300 whitespace-nowrap">{formatDate(busca.vigencia_fim)}</td>
                                                    <td className="px-[0.3rem] py-2 text-center text-gray-700 dark:text-gray-300">{busca.codigo ?? '—'}</td>
                                                    <td className="px-[0.3rem] py-2 text-center">
                                                      {busca.link_documento ? (
                                                        <a href={busca.link_documento} target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline text-xs" onClick={e => e.stopPropagation()}>Abrir</a>
                                                      ) : '—'}
                                                    </td>
                                                    <td className="px-[0.3rem] py-2 text-center">
                                                      {busca.link_download ? (
                                                        <a href={busca.link_download} target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline text-xs" onClick={e => e.stopPropagation()}>Baixar</a>
                                                      ) : '—'}
                                                    </td>
                                                    {canInsert && (
                                                      <td className="px-[0.3rem] py-2 text-center" onClick={e => e.stopPropagation()}>
                                                        {(busca.status || '').trim().toUpperCase() === 'DISPONÍVEL PARA IMPORTAR' ? (
                                                          (registros as ConvencaoData[])[0]?.id != null && (
                                                            <Tooltip content="Enviar para SCGP">
                                                              <button
                                                                type="button"
                                                                disabled={isEnviarScgpModalOpen}
                                                                onClick={() => handleEnviarParaScgp((registros as ConvencaoData[])[0]!.id, busca.id)}
                                                                className="inline-flex items-center justify-center rounded-md p-2 text-gray-600 hover:text-green-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-green-400 dark:hover:bg-gray-700 focus:outline-none transition-colors disabled:opacity-50 disabled:pointer-events-none"
                                                                aria-label="Enviar para SCGP"
                                                              >
                                                                <PaperAirplaneIcon className="h-5 w-5" />
                                                              </button>
                                                            </Tooltip>
                                                          )
                                                        ) : (
                                                          <span className="text-gray-400 dark:text-gray-500">—</span>
                                                        )}
                                                      </td>
                                                    )}
                                                  </tr>
                                                ))}
                                              </tbody>
                                            </table>
                                            </div>
                                          </div>
                                        )
                                      })()}
                                    </div>
                                  </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                          )
                          })}
                          {convencoesAgrupadasPaginadas.length === 0 && (
                            <tr>
                              <td colSpan={canInsert ? 13 : 11} className="px-6 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                                Nenhum registro encontrado
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  
                  {/* Controles de Paginação */}
                  {totalPagesAgrupadas > 1 && (
                    <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center text-sm text-gray-700 dark:text-gray-300">
                          <Text size="sm">
                            Mostrando {startIndexAgrupadas + 1} a {Math.min(endIndexAgrupadas, convencoesAgrupadas.length)} de {convencoesAgrupadas.length} registros agrupados
                          </Text>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={handlePreviousPage}
                            disabled={currentPage === 1}
                            className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                          >
                            Anterior
                          </button>
                          
                          <div className="flex space-x-1">
                            {Array.from({ length: Math.min(5, totalPagesAgrupadas) }, (_, i) => {
                              let pageNumber: number
                              if (totalPagesAgrupadas <= 5) {
                                pageNumber = i + 1
                              } else if (currentPage <= 3) {
                                pageNumber = i + 1
                              } else if (currentPage >= totalPagesAgrupadas - 2) {
                                pageNumber = totalPagesAgrupadas - 4 + i
                              } else {
                                pageNumber = currentPage - 2 + i
                              }
                              
                              return (
                                <button
                                  key={pageNumber}
                                  onClick={() => handlePageChange(pageNumber)}
                                  className={`px-3 py-2 text-sm font-medium rounded-md ${
                                    currentPage === pageNumber
                                      ? 'bg-blue-600 text-white'
                                      : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
                                  }`}
                                >
                                  {pageNumber}
                                </button>
                              )
                            })}
                          </div>
                          
                          <button
                            onClick={handleNextPage}
                            disabled={currentPage === totalPagesAgrupadas}
                            className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                          >
                            Próximo
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
      </div>

      {/* Modal Busca MTE: confirmação → carregamento; ao fechar atualiza dados (filtros mantidos) */}
      {isBuscaMteModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" />
            <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md sm:p-6">
              <div className="flex flex-col items-center gap-4 py-4">
                {buscaMteModalStatus === 'confirm' && (
                  <>
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/30">
                      <QuestionMarkCircleIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                    </div>
                    <Text size="sm" className="text-gray-700 dark:text-gray-300 text-center">
                      Deseja confirmar a busca desta convenção no MTE?
                    </Text>
                  </>
                )}
                {buscaMteModalStatus === 'loading' && (
                  <>
                    <LoadingSpinner size="sm" className="h-12 w-12 text-blue-600 dark:text-blue-400" />
                    <Text size="sm" className="text-gray-700 dark:text-gray-300 text-center">
                      Buscando no MTE...
                    </Text>
                  </>
                )}
                {buscaMteModalStatus === 'success' && (
                  <>
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
                      <svg className="h-6 w-6 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <Text size="sm" className="text-gray-700 dark:text-gray-300 text-center">
                      Busca acionada com sucesso. Feche para atualizar os dados.
                    </Text>
                  </>
                )}
                {buscaMteModalStatus === 'error' && (
                  <>
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
                      <ExclamationTriangleIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
                    </div>
                    <Text size="sm" className="text-gray-700 dark:text-gray-300 text-center">
                      {buscaMteModalError ?? 'Erro ao acionar a busca.'}
                    </Text>
                  </>
                )}
              </div>
              <div className="mt-4 flex justify-center gap-2">
                {buscaMteModalStatus === 'confirm' && (
                  <>
                    <Button variant="outline" size="sm" onClick={handleCancelBuscaMteModal}>
                      Cancelar
                    </Button>
                    <Button variant="solid" size="sm" onClick={handleConfirmBuscaMte}>
                      Confirmar
                    </Button>
                  </>
                )}
                {(buscaMteModalStatus === 'loading' || buscaMteModalStatus === 'success' || buscaMteModalStatus === 'error') && (
                  <Button variant="solid" size="sm" onClick={handleCloseBuscaMteModal}>
                    Fechar
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Busca MTE em massa: confirmação → carregamento (5 s entre cada); ao fechar atualiza dados e limpa seleção */}
      {isBuscaMteMassaModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" />
            <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md sm:p-6">
              <div className="flex flex-col items-center gap-4 py-4">
                {buscaMteMassaStatus === 'confirm' && (
                  <>
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/30">
                      <QuestionMarkCircleIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                    </div>
                    <Text size="sm" className="text-gray-700 dark:text-gray-300 text-center">
                      Deseja acionar a busca no MTE para {selectedForBuscaMteIds.size} convenção(ões)?
                    </Text>
                  </>
                )}
                {buscaMteMassaStatus === 'loading' && (
                  <>
                    <LoadingSpinner size="sm" className="h-12 w-12 text-blue-600 dark:text-blue-400" />
                    <Text size="sm" className="text-gray-700 dark:text-gray-300 text-center">
                      Buscando no MTE... ({buscaMteMassaProgress.concluidos} de {buscaMteMassaProgress.total}) - {buscaMteMassaPercentual}%
                    </Text>
                    <div className="w-full max-w-xs">
                      <div className="h-2 w-full rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                        <div
                          className="h-full bg-blue-600 dark:bg-blue-400 transition-all duration-500 ease-out"
                          style={{ width: `${buscaMteMassaPercentual}%` }}
                        />
                      </div>
                    </div>
                    {buscaMteMassaProgress.erros.length > 0 && (
                      <Text size="sm" className="text-red-600 dark:text-red-400 text-center">
                        {buscaMteMassaProgress.erros.length} falha(s) até o momento.
                      </Text>
                    )}
                  </>
                )}
                {buscaMteMassaStatus === 'success' && (
                  <>
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
                      <svg className="h-6 w-6 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <Text size="sm" className="text-gray-700 dark:text-gray-300 text-center">
                      Busca acionada para {buscaMteMassaProgress.concluidos} convenção(ões).
                      {buscaMteMassaProgress.erros.length > 0 && (
                        <> {buscaMteMassaProgress.erros.length} falha(s).</>
                      )}
                    </Text>
                  </>
                )}
                {buscaMteMassaStatus === 'error' && (
                  <>
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
                      <ExclamationTriangleIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
                    </div>
                    <Text size="sm" className="text-gray-700 dark:text-gray-300 text-center">
                      Nenhuma busca concluída com sucesso. {buscaMteMassaProgress.erros.length} falha(s).
                    </Text>
                    {buscaMteMassaProgress.erros.length > 0 && (
                      <ul className="text-xs text-left text-gray-600 dark:text-gray-400 list-disc pl-4 max-h-24 overflow-y-auto">
                        {buscaMteMassaProgress.erros.slice(0, 5).map((e) => (
                          <li key={e.id}>ID {e.id}: {e.message}</li>
                        ))}
                        {buscaMteMassaProgress.erros.length > 5 && (
                          <li>… e mais {buscaMteMassaProgress.erros.length - 5}</li>
                        )}
                      </ul>
                    )}
                  </>
                )}
              </div>
              <div className="mt-4 flex justify-center gap-2">
                {buscaMteMassaStatus === 'confirm' && (
                  <>
                    <Button variant="outline" size="sm" onClick={handleCancelBuscaMteMassaModal}>
                      Cancelar
                    </Button>
                    <Button variant="solid" size="sm" onClick={handleConfirmBuscaMteMassa}>
                      Confirmar
                    </Button>
                  </>
                )}
                {(buscaMteMassaStatus === 'success' || buscaMteMassaStatus === 'error') && (
                  <Button variant="solid" size="sm" onClick={handleCloseBuscaMteMassaModal}>
                    Fechar
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Enviar para SCGP: confirmação → carregamento; ao fechar atualiza dados */}
      {isEnviarScgpModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" />
            <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md sm:p-6">
              <div className="flex flex-col items-center gap-4 py-4">
                {enviarScgpModalStatus === 'confirm' && (
                  <>
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
                      <QuestionMarkCircleIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
                    </div>
                    <Text size="sm" className="text-gray-700 dark:text-gray-300 text-center">
                      Deseja confirmar o envio desta convenção para o SCGP?
                    </Text>
                  </>
                )}
                {enviarScgpModalStatus === 'loading' && (
                  <>
                    <LoadingSpinner size="sm" className="h-12 w-12 text-green-600 dark:text-green-400" />
                    <Text size="sm" className="text-gray-700 dark:text-gray-300 text-center">
                      Enviando para o SCGP...
                    </Text>
                  </>
                )}
                {enviarScgpModalStatus === 'success' && (
                  <>
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
                      <svg className="h-6 w-6 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <Text size="sm" className="text-gray-700 dark:text-gray-300 text-center">
                      Envio realizado com sucesso. Feche para atualizar os dados.
                    </Text>
                  </>
                )}
                {enviarScgpModalStatus === 'error' && (
                  <>
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
                      <ExclamationTriangleIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
                    </div>
                    <Text size="sm" className="text-gray-700 dark:text-gray-300 text-center">
                      {enviarScgpModalError ?? 'Erro ao enviar para o SCGP.'}
                    </Text>
                  </>
                )}
              </div>
              <div className="mt-4 flex justify-center gap-2">
                {enviarScgpModalStatus === 'confirm' && (
                  <>
                    <Button variant="outline" size="sm" onClick={handleCancelEnviarScgpModal}>
                      Cancelar
                    </Button>
                    <Button variant="solid" size="sm" onClick={handleConfirmEnviarScgp}>
                      Confirmar
                    </Button>
                  </>
                )}
                {(enviarScgpModalStatus === 'loading' || enviarScgpModalStatus === 'success' || enviarScgpModalStatus === 'error') && (
                  <Button variant="solid" size="sm" onClick={handleCloseEnviarScgpModal}>
                    Fechar
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal detalhes do registro do Histórico de buscas no MTE */}
      {isHistoricoBuscaModalOpen && selectedBuscaHistorico && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={() => { setIsHistoricoBuscaModalOpen(false); setSelectedBuscaHistorico(null); setHistoricoBuscaModalTab('dados') }} />
            <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:p-6 max-h-[90vh] flex flex-col">
              <div className="flex items-center justify-between mb-4">
                <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                  Registro do Histórico de buscas no MTE
                </Heading>
                <button
                  type="button"
                  onClick={() => { setIsHistoricoBuscaModalOpen(false); setSelectedBuscaHistorico(null); setHistoricoBuscaModalTab('dados') }}
                  className="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none dark:hover:text-gray-300"
                  aria-label="Fechar"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              <div className="flex border-b border-gray-200 dark:border-gray-600 mb-4">
                <button
                  type="button"
                  onClick={() => setHistoricoBuscaModalTab('dados')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${historicoBuscaModalTab === 'dados' ? 'border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'}`}
                >
                  Dados
                </button>
                <button
                  type="button"
                  onClick={() => setHistoricoBuscaModalTab('resposta')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${historicoBuscaModalTab === 'resposta' ? 'border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'}`}
                >
                  Resposta
                </button>
                <button
                  type="button"
                  onClick={() => setHistoricoBuscaModalTab('clausulas')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${historicoBuscaModalTab === 'clausulas' ? 'border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'}`}
                >
                  Cláusulas específicas
                </button>
              </div>
              <div className="overflow-y-auto flex-1 min-h-0">
                {historicoBuscaModalTab === 'dados' ? (
                  <div className="space-y-3 text-sm">
                    {[
                      { label: 'ID', value: selectedBuscaHistorico.id },
                      { label: 'Data', value: formatDate(selectedBuscaHistorico.created_at) },
                      { label: 'CCT Convenção ID', value: selectedBuscaHistorico.cct_convencao_id != null ? String(selectedBuscaHistorico.cct_convencao_id) : '—' },
                      { label: 'Status', value: (() => {
                        const s = (selectedBuscaHistorico.status || '').trim().toUpperCase()
                        if (s === 'NÃO LOCALIZADA MTE' || s === 'NAO LOCALIZADA MTE' || s === 'NÃO LOCALIZADA' || s === 'NAO LOCALIZADA') return 'NÃO LOCALIZADA'
                        return selectedBuscaHistorico.status ?? '—'
                      })() },
                      { label: 'Link documento', value: selectedBuscaHistorico.link_documento ? (
                        <a href={selectedBuscaHistorico.link_documento} target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline" onClick={e => e.stopPropagation()}>Abrir</a>
                      ) : '—' },
                      { label: 'Link download', value: selectedBuscaHistorico.link_download ? (
                        <a href={selectedBuscaHistorico.link_download} target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline" onClick={e => e.stopPropagation()}>Baixar</a>
                      ) : '—' },
                      { label: 'Vigência início', value: formatDate(selectedBuscaHistorico.vigencia_inicio) },
                      { label: 'Vigência fim', value: formatDate(selectedBuscaHistorico.vigencia_fim) },
                      { label: 'Partes', value: selectedBuscaHistorico.partes ?? '—' },
                      { label: 'N° MTE', value: selectedBuscaHistorico.codigo ?? '—' },
                      { label: 'Data importação', value: selectedBuscaHistorico.data_importacao ? formatDate(selectedBuscaHistorico.data_importacao) : '—' },
                      { label: 'ID SCGP', value: selectedBuscaHistorico.id_scgp != null ? String(selectedBuscaHistorico.id_scgp) : '—' },
                    ].map(({ label, value }) => (
                      <div key={label} className="flex flex-col gap-0.5">
                        <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">{label}</span>
                        <span className="text-gray-700 dark:text-gray-300 break-words">{value}</span>
                      </div>
                    ))}
                  </div>
                ) : historicoBuscaModalTab === 'resposta' ? (
                  <div className="text-sm">
                    {(() => {
                      const v = selectedBuscaHistorico.resposta
                      const s = v == null ? '' : String(v).trim()
                      const empty = s === '' || s.toLowerCase() === 'null' || s.toLowerCase() === 'undefined'
                      return empty ? (
                        <span className="text-gray-500 dark:text-gray-400">Nenhum conteúdo.</span>
                      ) : (
                        <pre className="whitespace-pre-wrap break-words font-sans text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-900/50 p-3 rounded-lg overflow-x-auto">{v}</pre>
                      )
                    })()}
                  </div>
                ) : (
                  <div className="text-sm">
                    {(() => {
                      const v = selectedBuscaHistorico.clausulas_especificas
                      const s = v == null ? '' : String(v).trim()
                      const empty = s === '' || s.toLowerCase() === 'null' || s.toLowerCase() === 'undefined'
                      return empty ? (
                        <span className="text-gray-500 dark:text-gray-400">Nenhum conteúdo.</span>
                      ) : (
                        <pre className="whitespace-pre-wrap break-words font-sans text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-900/50 p-3 rounded-lg overflow-x-auto">{v}</pre>
                      )
                    })()}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Detalhes */}
      {isDetailsModalOpen && selectedConvencao && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleCloseDetailsModal}></div>
            
            <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-6xl sm:p-6 max-h-[90vh] flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <div>
                  <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                    Detalhes da Convenção SCGP
                  </Heading>
                </div>
                <button
                  onClick={handleCloseDetailsModal}
                  className="rounded-md bg-white dark:bg-gray-800 text-gray-400 hover:text-gray-500 focus:outline-none"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              {/* Tabela de Detalhes */}
              <div className="flex-1 overflow-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Campo
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Valor
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-left">
                        <Text size="sm" className="text-gray-900 dark:text-white font-medium">
                          ID
                        </Text>
                      </td>
                      <td className="px-6 py-4 text-left">
                        <Text size="sm" className="text-gray-700 dark:text-gray-300 font-mono">
                          {selectedConvencao.id}
                        </Text>
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-left">
                        <Text size="sm" className="text-gray-900 dark:text-white font-medium">
                          Criado em
                        </Text>
                      </td>
                      <td className="px-6 py-4 text-left">
                        <Text size="sm" className="text-gray-700 dark:text-gray-300">
                          {formatDate(selectedConvencao.created_at)}
                        </Text>
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-left">
                        <Text size="sm" className="text-gray-900 dark:text-white font-medium">
                          CNPJ Entidade Patronal
                        </Text>
                      </td>
                      <td className="px-6 py-4 text-left">
                        <Text size="sm" className="text-gray-700 dark:text-gray-300 font-mono">
                          {selectedConvencao.cnpj_entidade_patronal || '—'}
                        </Text>
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-left">
                        <Text size="sm" className="text-gray-900 dark:text-white font-medium">
                          CNPJ Entidade Laboral
                        </Text>
                      </td>
                      <td className="px-6 py-4 text-left">
                        <Text size="sm" className="text-gray-700 dark:text-gray-300 font-mono">
                          {selectedConvencao.cnpj_entidade_laboral || '—'}
                        </Text>
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-left">
                        <Text size="sm" className="text-gray-900 dark:text-white font-medium">
                          Nº Vidas
                        </Text>
                      </td>
                      <td className="px-6 py-4 text-left">
                        <Text size="sm" className="text-gray-700 dark:text-gray-300">
                          {selectedConvencao.num_vidas != null ? selectedConvencao.num_vidas : '—'}
                        </Text>
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-left">
                        <Text size="sm" className="text-gray-900 dark:text-white font-medium">
                          Nº Subgrupos
                        </Text>
                      </td>
                      <td className="px-6 py-4 text-left">
                        <Text size="sm" className="text-gray-700 dark:text-gray-300">
                          {selectedConvencao.convenios_qt != null ? selectedConvencao.convenios_qt : '—'}
                        </Text>
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-left">
                        <Text size="sm" className="text-gray-900 dark:text-white font-medium">
                          ID SCGP
                        </Text>
                      </td>
                      <td className="px-6 py-4 text-left">
                        <Text size="sm" className="text-gray-700 dark:text-gray-300">
                          {selectedConvencao.id_scgp ?? '—'}
                        </Text>
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-left">
                        <Text size="sm" className="text-gray-900 dark:text-white font-medium">
                          Data Início Vigência
                        </Text>
                      </td>
                      <td className="px-6 py-4 text-left">
                        <Text size="sm" className="text-gray-700 dark:text-gray-300">
                          {formatDate(selectedConvencao.data_inicio_vigencia)}
                        </Text>
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-left">
                        <Text size="sm" className="text-gray-900 dark:text-white font-medium">
                          Data Fim Vigência
                        </Text>
                      </td>
                      <td className="px-6 py-4 text-left">
                        <Text size="sm" className="text-gray-700 dark:text-gray-300">
                          {formatDate(selectedConvencao.data_fim_vigencia)}
                        </Text>
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-left">
                        <Text size="sm" className="text-gray-900 dark:text-white font-medium">
                          Status
                        </Text>
                      </td>
                      <td className="px-6 py-4 text-left">
                        <Text size="sm" className={(selectedConvencao.status || '').toUpperCase() === 'EXPIRADA' ? 'text-red-600 dark:text-red-400 font-semibold' : (selectedConvencao.status || '').toUpperCase() === 'VIGENTE' ? 'text-green-600 dark:text-green-400 font-semibold' : (selectedConvencao.status || '').toUpperCase() === 'BUSCADA' || (selectedConvencao.status || '').toUpperCase() === 'SUBSTITUIDA' ? 'text-blue-600 dark:text-blue-400 font-semibold' : 'text-gray-700 dark:text-gray-300'}>
                          {selectedConvencao.status || '—'}
                        </Text>
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-left">
                        <Text size="sm" className="text-gray-900 dark:text-white font-medium">
                          Data Última Busca
                        </Text>
                      </td>
                      <td className="px-6 py-4 text-left">
                        <Text size="sm" className="text-gray-700 dark:text-gray-300">
                          {formatDateTime(selectedConvencao.data_ultima_busca)}
                        </Text>
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-left">
                        <Text size="sm" className="text-gray-900 dark:text-white font-medium">
                          Categoria
                        </Text>
                      </td>
                      <td className="px-6 py-4 text-left">
                        <Text size="sm" className="text-gray-700 dark:text-gray-300">
                          {getNomeCategoria(selectedConvencao.cod_categoria)}
                        </Text>
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-left">
                        <Text size="sm" className="text-gray-900 dark:text-white font-medium">
                          Descrição Territorial
                        </Text>
                      </td>
                      <td className="px-6 py-4 text-left">
                        <Text size="sm" className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words">
                          {selectedConvencao.desc_territorial || '—'}
                        </Text>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default CCTSDashboardScgp
